/* @ts-nocheck */
/* global google */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  GoogleMap,
  useLoadScript,
  PolylineF,
  MarkerF,
  InfoWindowF
} from '@react-google-maps/api';
import { supabase } from '../utils/supabaseClient';

// Add deck.gl imports
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, LineLayer, TextLayer, ColumnLayer, ScatterplotLayer } from '@deck.gl/layers';

import { LineChart, Table2 } from 'lucide-react';
import { IconLayer } from '@deck.gl/layers';
import { Camera } from 'lucide-react';
import MapCameraControls, { NavbarButton } from './MapCameraControls';
import ALRInfoPanel from './ALRInfoPanel';
import SegmentInfoPanel from './SegmentInfoPanel';
import PlaybackDrawer from './PlaybackDrawer';
import KmlChartsDrawer from './KmlChartsDrawer';
import DataTableDrawer from './DataTableDrawer';
import BumpDipInfoPanel from './BumpDipInfoPanel';
import { MagnifyingGlassCircleIcon } from '@heroicons/react/24/outline';
import { PathStyleExtension } from '@deck.gl/extensions';


const LIBRARIES = ['geometry'];

const LOLLIPOP_HEIGHT_SCALE = 8;
const LOLLIPOP_ELEVATION = 3;
const COMPASS_TICKS = Array.from({ length: 24 }, (_, index) => index * 15);
const COMPASS_LABELS = [
  { label: 'N', angle: 0, className: 'text-red-600 font-extrabold' },
  { label: 'E', angle: 90, className: 'text-gray-700 font-bold' },
  { label: 'S', angle: 180, className: 'text-gray-700 font-bold' },
  { label: 'W', angle: 270, className: 'text-gray-700 font-bold' },
];
const STATION_MARKER_MIN_ZOOM = 15;
const STATION_MARKER_DENSE_ZOOM = 17;
const STATION_MARKER_SPARSE_INTERVAL_FT = 500;
const STATION_MARKER_DENSE_INTERVAL_FT = 100;
const STATION_MARKER_MAX_LABELS = 200;

const normalizeHeading = (heading) => {
  const value = Number.isFinite(heading) ? heading : 0;
  return ((value % 360) + 360) % 360;
};

const formatStationMarkerLabel = (station) => {
  const value = Number(station);
  if (!Number.isFinite(value)) return '';

  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);
  const hundreds = Math.floor(absValue / 100);
  const remainder = absValue - hundreds * 100;
  const roundedRemainder = Math.round(remainder * 10) / 10;
  const remainderText = Math.abs(roundedRemainder - Math.round(roundedRemainder)) < 0.05
    ? String(Math.round(roundedRemainder)).padStart(2, '0')
    : roundedRemainder.toFixed(1).padStart(4, '0');

  return `${sign}${hundreds}+${remainderText}`;
};

const CompassRose = React.memo(({ heading, onResetNorth }) => {
  const normalizedHeading = normalizeHeading(heading);

  return (
    <button
      type="button"
      onClick={onResetNorth}
      title="Reset map north"
      aria-label="Reset map north"
      className="group h-[76px] w-[76px] rounded-full border border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
    >
      <div className="relative h-16 w-16 rounded-full border border-gray-300 bg-gradient-to-b from-gray-50 to-white shadow-inner overflow-hidden">
        <div className="absolute inset-[5px] rounded-full border border-gray-100" />
        {COMPASS_TICKS.map((angle) => {
          const displayAngle = angle - normalizedHeading;
          const isCardinal = angle % 90 === 0;
          const isMajor = angle % 45 === 0;

          return (
            <span
              key={angle}
              className={`absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 rounded-full ${
                isCardinal
                  ? 'h-2.5 w-0.5 bg-gray-900'
                  : isMajor
                    ? 'h-2 w-0.5 bg-gray-500'
                    : 'h-1.5 w-px bg-gray-300'
              }`}
              style={{
                transform: `translate(-50%, -50%) rotate(${displayAngle}deg) translateY(-27px)`,
                transformOrigin: 'center',
              }}
            />
          );
        })}
        {COMPASS_LABELS.map(({ label, angle, className }) => {
          const displayAngle = angle - normalizedHeading;

          return (
            <span
              key={label}
              className={`absolute left-1/2 top-1/2 text-[10px] leading-none tracking-normal ${className}`}
              style={{
                transform: `translate(-50%, -50%) rotate(${displayAngle}deg) translateY(-19px) rotate(${-displayAngle}deg)`,
                transformOrigin: 'center',
              }}
            >
              {label}
            </span>
          );
        })}
        <div className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center">
          <div className="h-1.5 w-1.5 rounded-full bg-gray-700 group-hover:bg-gray-900" />
        </div>
      </div>
    </button>
  );
});

function buildLollipopLayers({
  events, layerVisibility, wheelpathFilter, selectedBumpDip,
  hoveredEventId, parseGeometry, interpolatePositionAtStation, formatStation,
  isProjectMap, setHoveredEventId, setSelectedBumpDip, setSelectedALR, setSelectedSegment, deckClickRef,
  type, idPrefix, color, hoverColor,
}) {
  const typeUpper = type.toUpperCase();
  let visibleEvents = (events || []).filter(e => {
    const t = (e.event_type || '').toUpperCase();
    if (t !== typeUpper || !layerVisibility[typeUpper]) return false;
    return (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
           (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
           !e.wheelpath;
  });


  if (visibleEvents.length === 0) return [];

  // One data point per event at peak station
  const lollipopData = visibleEvents.map(event => {
    const from = event.from_station_ft;
    const to = event.to_station_ft;
    const peak = event.peak_station_ft || from;
    const magnitude = event.magnitude_in || 0;
    const geometry = parseGeometry(event.geom);
    if (!geometry) return null;
    const position = interpolatePositionAtStation(geometry, from, to, peak);
    if (!position) return null;
    return { ...event, position, height: magnitude * LOLLIPOP_HEIGHT_SCALE };
  }).filter(Boolean);

  const layers = [];

  // Stem — thin extruded column from ground to height
  layers.push(
    new ColumnLayer({
      id: `${idPrefix}-stem`,
      data: lollipopData,
      getPosition: d => {
        const [lng, lat] = d.position;
        const baseZ = 0;
        return [lng, lat, baseZ];
      },
      getElevation: d => d.height,
      elevationScale: 1,
      getFillColor: d => {
        const isHovered = d.event_id === hoveredEventId;
        return isHovered ? [...hoverColor, 220] : [...color, 180];
      },
      radius: 0.15,
      radiusUnits: 'meters',
      radiusMinPixels: 2,
      radiusMaxPixels: 6,
      diskResolution: 8,
      extruded: true,
      flatShading: true,
      material: { ambient: 0.8, diffuse: 0.2, shininess: 0 },
      parameters: { depthTest: false },
      pickable: true,
      onHover: ({ object }) => { setHoveredEventId(object ? object.event_id : null); },
      onClick: ({ object }) => {
        if (object) {
          deckClickRef.current = true;
          setSelectedBumpDip(object);
          setSelectedALR(null);
          setSelectedSegment(null);
        }
      },
    })
  );

  // Sphere — ScatterplotLayer at top of stem
  layers.push(
    new ScatterplotLayer({
      id: `${idPrefix}-sphere`,
      data: lollipopData,
      getPosition: d => {
        const [lng, lat] = d.position;
        const baseZ = 0;
        return [lng, lat, baseZ + d.height];
      },
      getRadius: d => {
        const isSelected = selectedBumpDip && d.event_id === selectedBumpDip.event_id;
        const isHovered = d.event_id === hoveredEventId;
        return isSelected ? 0.8 : isHovered ? 0.6 : 0.4;
      },
      radiusUnits: 'meters',
      radiusMinPixels: 5,
      radiusMaxPixels: 12,
      getFillColor: d => {
        const isHovered = d.event_id === hoveredEventId;
        return isHovered ? [...hoverColor, 255] : [...color, 255];
      },
      getLineColor: d => {
        const isSelected = selectedBumpDip && d.event_id === selectedBumpDip.event_id;
        return isSelected ? [255, 255, 255, 255] : [0, 0, 0, 80];
      },
      getLineWidth: d => {
        const isSelected = selectedBumpDip && d.event_id === selectedBumpDip.event_id;
        return isSelected ? 2 : 1;
      },
      stroked: true,
      lineWidthUnits: 'pixels',
      pickable: true,
      parameters: { depthTest: false },
      onHover: ({ object }) => { setHoveredEventId(object ? object.event_id : null); },
      onClick: ({ object }) => {
        if (object) {
          deckClickRef.current = true;
          setSelectedBumpDip(object);
          setSelectedALR(null);
          setSelectedSegment(null);
        }
      },
    })
  );

  return layers;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 42.0308, // Iowa State University
  lng: -93.6319
};

// Session colors for project maps
const SESSION_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

// Constants for 3D IRI Dimensions
const DIMENSION_ALTITUDE = 0; // in meters
const DIMENSION_OFFSET = 0;  // in meters, to push the line sideways off the road center
const IRI_TEXT_ZOOM_THRESHOLD = 15; // Zoom level at which IRI text appears
const IRI_TEXT_COLOR = [0, 0, 0, 255]; // Black text
const IRI_TEXT_BACKGROUND_COLOR = [255, 255, 255, 255]; // White background

const ControlPanel = React.memo(({
  layerVisibility,
  setLayerVisibility,
  kmlProfileMode,
  setKmlProfileMode,
  wheelpathFilter,
  setWheelpathFilter,
  mapStyleMode,
  setMapStyleMode,
  adjustMap,
  mapRef,
  isKmlLoading,
  isPhotoLoading,
  isPlaybackActive,
  onStartPlayback,
  hasPhotos,
  iriProfileData,
  elevationProfileData,
  handleStartKmlScrub,
  isKmlScrubActive,
  segments,
  isFullscreen,
  onToggleFullscreen,
  onToggleDataTable,
  isDataTableActive,
 }) => {
  
  const [localTooltip, setLocalTooltip] = useState(null);
  const layerBarRef = useRef(null);

 const handleToggle = useCallback((layer) => {
    if (layer === 'SEGMENTS' || layer === 'LR' || layer === 'IRI_ROADWAY') {
      setLayerVisibility(prev => ({
        ...prev,
        SEGMENTS: layer === 'SEGMENTS' ? !prev.SEGMENTS : false,
        LR: layer === 'LR' ? !prev.LR : false,
        IRI_ROADWAY: layer === 'IRI_ROADWAY' ? !prev.IRI_ROADWAY : false
      }));
    } else {
      setLayerVisibility(prev => ({ ...prev, [layer]: !prev[layer] }));
    }
  }, [setLayerVisibility]);

  const handleWheelpathToggle = useCallback((wheelpath) => {
    setWheelpathFilter(prev => ({
      ...prev,
      [wheelpath]: !prev[wheelpath]
    }));
  }, [setWheelpathFilter]);

  const zoomIn = useCallback(() => mapRef.current && mapRef.current.setZoom(Math.min(mapRef.current.getZoom() + 1, 22)), [mapRef]);
  const zoomOut = useCallback(() => mapRef.current && mapRef.current.setZoom(Math.max(mapRef.current.getZoom() - 1, 10)), [mapRef]);

  const hasKmlData = (iriProfileData || elevationProfileData);

  const resetHeading = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.setHeading(0);
    mapRef.current.setTilt(0);
  }, [mapRef]);

  return (
    <>
      {/* Top-left map style toggle */}
      <div className="absolute top-3 left-3 z-[1001]" style={{ pointerEvents: 'auto' }} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <div className="flex items-center space-x-1 p-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <button
            onClick={() => setMapStyleMode('satellite')}
            className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
              mapStyleMode === 'satellite' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Satellite
          </button>
          <button
            onClick={() => setMapStyleMode('roadmap')}
            className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
              mapStyleMode === 'roadmap' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Map
          </button>
        </div>
      </div>

      {/* Layer controls - bottom center */}
      <div
        ref={layerBarRef}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center rounded-lg gap-1.5 bg-white border border-gray-200 shadow-lg px-1.5 py-1 z-[1001]"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {/* Data View */}
        <div className="flex items-center gap-1">
          <NavbarButton tooltipText="Road Segments" onClick={() => handleToggle('SEGMENTS')} isActive={layerVisibility.SEGMENTS} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">SEG</p>
          </NavbarButton>
          <NavbarButton tooltipText="ALRs" onClick={() => handleToggle('LR')} isActive={layerVisibility.LR} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">ALR</p>
          </NavbarButton>
          <NavbarButton tooltipText="IRI Wheelpath" onClick={() => handleToggle('IRI_ROADWAY')} isActive={layerVisibility.IRI_ROADWAY} disabled={!iriProfileData} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">IRI</p>
          </NavbarButton>
          <NavbarButton tooltipText="Left Wheelpath" onClick={() => handleWheelpathToggle('LEFT')} isActive={wheelpathFilter.LEFT} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">L</p>
          </NavbarButton>
          <NavbarButton tooltipText="Right Wheelpath" onClick={() => handleWheelpathToggle('RIGHT')} isActive={wheelpathFilter.RIGHT} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">R</p>
          </NavbarButton>
        </div>

        <div className="w-px h-5 bg-gray-200" />

        {/* Overlays */}
        <div className="flex items-center gap-1">
	          <NavbarButton tooltipText="Exception Areas" onClick={() => handleToggle('EXCEPTIONS')} isActive={layerVisibility.EXCEPTIONS} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
	            <p className="font-semibold text-xs">EXC</p>
	          </NavbarButton>
	          <NavbarButton tooltipText="Station Markers" onClick={() => handleToggle('STATIONS')} isActive={layerVisibility.STATIONS} disabled={!segments || segments.length === 0} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
	            <p className="font-semibold text-xs">STA</p>
	          </NavbarButton>
	          <NavbarButton tooltipText="Bumps" onClick={() => handleToggle('BUMP')} isActive={layerVisibility.BUMP} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
	            <p className="font-semibold text-xs">BUMP</p>
	          </NavbarButton>
          <NavbarButton tooltipText="Dips" onClick={() => handleToggle('DIP')} isActive={layerVisibility.DIP} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <p className="font-semibold text-xs">DIP</p>
          </NavbarButton>
        </div>

        <div className="w-px h-5 bg-gray-200" />

        {/* Tools */}
        <div className="flex items-center gap-1">
          {(hasKmlData || isKmlLoading) && (
            <NavbarButton tooltipText={layerVisibility.KML ? "Hide 3D KML" : "Show 3D KML"} onClick={() => handleToggle('KML')} isActive={layerVisibility.KML} isLoading={isKmlLoading} disabled={!hasKmlData} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
              <p className="font-semibold text-xs">3D</p>
            </NavbarButton>
          )}
          <NavbarButton tooltipText={hasPhotos ? "Photo Replay" : "No photos available"} onClick={onStartPlayback} isActive={isPlaybackActive} isLoading={isPhotoLoading} disabled={!hasPhotos && !isPhotoLoading} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <Camera className="h-4 w-4" />
          </NavbarButton>
          {hasKmlData && (
            <NavbarButton tooltipText={isKmlScrubActive ? "Close Chart Analysis" : "Open Chart Analysis"} onClick={handleStartKmlScrub} isActive={isKmlScrubActive} disabled={!segments || segments.length === 0} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
              <LineChart className="h-4 w-4" />
            </NavbarButton>
          )}
          <NavbarButton tooltipText={isDataTableActive ? "Close Data Table" : "Data Table"} onClick={onToggleDataTable} isActive={isDataTableActive} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={layerBarRef}>
            <Table2 className="h-4 w-4" />
          </NavbarButton>
        </div>
      </div>

      <MapCameraControls adjustMap={adjustMap} zoomIn={zoomIn} zoomOut={zoomOut} resetHeading={resetHeading} onToggleFullscreen={onToggleFullscreen} isFullscreen={isFullscreen} />

      {localTooltip && (
         <div className="fixed bg-gray-900 text-white text-xs rounded-md px-2 py-1 shadow-lg z-[2002] pointer-events-none whitespace-nowrap" style={{ top: localTooltip.y, left: localTooltip.x, transform: 'translateX(-50%)' }}>
           {localTooltip.text}
         </div>
      )}
    </>
  );
});

function FusionMap({ sessionId, segments, events, bounds, session, projectConfig, iriProfileData = null,
  elevationProfileData = null,
  kmlLoading = false,
  kmlError = null,
  kmlMarkers = null,
  onAnalyzeSegment
}) {
  const mapRef = useRef(null);
  const hasOrientedRef = useRef(false);

  // Core map state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(15);
  const [currentZoom, setCurrentZoom] = useState(15); // State to track current zoom
  const [mapStyleMode, setMapStyleMode] = useState('satellite');

  // Data visualization state
  const [eventPolylines, setEventPolylines] = useState([]);
  const [indicators, setIndicators] = useState([]);

  // UI state
  const [infoWindow, setInfoWindow] = useState(null);
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const [legendIriFilter, setLegendIriFilter] = useState(null); // null = show all, index = show only that range

  // Loading and error state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [photoMarkers, setPhotoMarkers] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [photoCatalogRequested, setPhotoCatalogRequested] = useState(false);
  const [photoCatalogLoaded, setPhotoCatalogLoaded] = useState(false);
  const [pendingPlaybackStart, setPendingPlaybackStart] = useState(false);
const [hoveredPhoto, setHoveredPhoto] = useState(null);
const [selectedPhoto, setSelectedPhoto] = useState(null);
const [selectedALR, setSelectedALR] = useState(null);
const [selectedSegment, setSelectedSegment] = useState(null);
const [selectedBumpDip, setSelectedBumpDip] = useState(null);
const [kmlProfileMode, setKmlProfileMode] = useState('iri');

// Add these to your existing state declarations near the top
const [isPlaybackActive, setIsPlaybackActive] = useState(false);
const [playbackIndex, setPlaybackIndex] = useState(0);
const [isPlaying, setIsPlaying] = useState(false);
const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1 = 1x, 2 = 2x, etc.
const [isCameraLocked, setIsCameraLocked] = useState(true); // For "Follow Mode"

const [isDrawerAnimating, setIsDrawerAnimating] = useState(false);

const [isKmlScrubActive, setIsKmlScrubActive] = useState(false);
const [isDataTableActive, setIsDataTableActive] = useState(false);
const [kmlScrubStation, setKmlScrubStation] = useState(null);
const [isKmlScrubPlaying, setIsKmlScrubPlaying] = useState(false);
const [kmlScrubSpeed, setKmlScrubSpeed] = useState(1);
const [isKmlScrubCameraLocked, setIsKmlScrubCameraLocked] = useState(true);

const [kmlIRIData, setKmlIRIData] = useState(null);
const [kmlElevationData, setKmlElevationData] = useState(null);

const mapContainerRef = useRef(null);
const fullscreenContainerRef = useRef(null);
const [isFullscreen, setIsFullscreen] = useState(false);

const [kmlTargetRange, setKmlTargetRange] = useState(null);

const [hoveredStation, setHoveredStation] = useState(null);

const isReversed = useMemo(() => session?.meta?.is_reverse_stationing === true, [session]);

const handleShowSegmentOnChart = useCallback(() => {
    if (!selectedSegment) return;

    const valA = selectedSegment.from_station_ft;
    const valB = selectedSegment.to_station_ft;

    const start = isReversed ? Math.max(valA, valB) : Math.min(valA, valB);
    const end = isReversed ? Math.min(valA, valB) : Math.max(valA, valB); // Logic used for bounds

    setIsPlaybackActive(false); // <--- Add this line
    setIsKmlScrubActive(true);
    
    setKmlScrubStation(start); 
    setIsKmlScrubPlaying(false);

    const buffer = 50; 
    setKmlTargetRange([Math.min(valA, valB) - buffer, Math.max(valA, valB) + buffer]);

}, [selectedSegment, isReversed]);

  useEffect(() => {
    if (isKmlScrubActive && selectedSegment) {
       // Use the helper function to ensure direction logic is applied
       handleShowSegmentOnChart();
    }
  }, [selectedSegment, isKmlScrubActive, handleShowSegmentOnChart]);

const getALRColor = (magnitude) => {
  if (magnitude > 250) return [244, 67, 54, 180];   // Red, 70% opacity
  if (magnitude > 200) return [255, 152, 0, 180];  // Orange, 70% opacity
  return [255, 193, 7, 180];                       // Amber, 70% opacity
};

const handleStartPlayback = useCallback(() => {
  setIsKmlScrubActive(false);
  setIsDataTableActive(false);
  setPlaybackIndex(0);
  setIsPlaying(false);
  setIsCameraLocked(true);
  if (photoCatalogLoaded && photoMarkers.length > 0) {
    setIsPlaybackActive(true);
    return;
  }
  setPendingPlaybackStart(true);
  setPhotoCatalogRequested(true);
}, [photoCatalogLoaded, photoMarkers.length]);

// Fullscreen toggle
const toggleFullscreen = useCallback(() => {
  if (!fullscreenContainerRef.current) return;

  if (!document.fullscreenElement) {
    fullscreenContainerRef.current.requestFullscreen().catch((err) => {
      console.error('Error attempting to enable fullscreen:', err);
    });
  } else {
    document.exitFullscreen();
  }
}, []);

// Listen for fullscreen change events to sync state
useEffect(() => {
  const handleFullscreenChange = () => {
    setIsFullscreen(!!document.fullscreenElement);
  };

  document.addEventListener('fullscreenchange', handleFullscreenChange);
  return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
}, []);

  // Layer visibility
  const [layerVisibility, setLayerVisibility] = useState({
	    SEGMENTS: true,
	    EXCEPTIONS: false,
	    STATIONS: true,
	    BUMP: false,
    DIP: false,
    LR: false,
    KML: false,
    IRI_ROADWAY: false,
  });

  // KML-related state
const [iriAltitudeScale, setIriAltitudeScale] = useState(0.5);
const [elevationAltitudeScale, setElevationAltitudeScale] = useState(1);
const deckOverlayRef = useRef(null);
  const deckClickRef = useRef(false);





const [wheelpathFilter, setWheelpathFilter] = useState({
  LEFT: true,
  RIGHT: true
});

  const [hoveredEventId, setHoveredEventId] = useState(null);

  // Map status for 3D controls
  const [mapStatus, setMapStatus] = useState({
    zoom: 15,
    tilt: 60,
    heading: 315,
  });
  const [currentHeading, setCurrentHeading] = useState(315);

  // Determine if this is a project-level map
  const isProjectMap = sessionId?.toString().startsWith('project-');

  // Extract unique sessions for project maps
  const uniqueSessions = useMemo(() => {
    if (!isProjectMap) return [];

    const sessionMap = new Map();
    [...segments, ...events].forEach(item => {
      if (item.sessionInfo && item.sessionInfo.session_id) {
        sessionMap.set(item.sessionInfo.session_id, item.sessionInfo);
      }
    });

    return Array.from(sessionMap.values());
  }, [segments, events, isProjectMap]);

  // Create session color mapping
  const sessionColorMap = useMemo(() => {
    const colorMap = new Map();
    uniqueSessions.forEach((sessionInfo, index) => {
      colorMap.set(sessionInfo.session_id, SESSION_COLORS[index % SESSION_COLORS.length]);
    });
    return colorMap;
  }, [uniqueSessions]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: 'beta',
  });

  const parseGeometry = useCallback((geom) => {
    try {
      if (typeof geom === 'object' && geom?.type === 'LineString' && geom.coordinates) {
        return { type: 'LineString', coordinates: geom.coordinates };
      }
      if (typeof geom === 'string') {
        if (geom.startsWith('LINESTRING')) {
          const coordsStr = geom.replace('LINESTRING(', '').replace(')', '');
          const coords = coordsStr.split(',').map(pair => {
            const [lng, lat] = pair.trim().split(' ').map(parseFloat);
            return [lng, lat];
          }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
          return { type: 'LineString', coordinates: coords };
        } else if (geom.startsWith('{') || geom.startsWith('[')) {
          const parsed = JSON.parse(geom);
          if (parsed?.type === 'LineString' && parsed.coordinates) {
            return { type: 'LineString', coordinates: parsed.coordinates };
          }
        }
      }
      throw new Error('Invalid geometry format');
    } catch (err) {
      console.error('Error parsing geometry:', err);
      return null;
    }
  }, []);

  // Helper function to interpolate position along geometry based on station
const interpolatePositionAtStation = (geometry, fromStation, toStation, targetStation) => {
  if (!geometry || geometry.coordinates.length === 0) {
    return null;
  }
  
  const coords = geometry.coordinates;
  
  if (coords.length === 1) {
    return coords[0];
  }
  
  const totalDistance = toStation - fromStation;
  if (totalDistance === 0) {
    return coords[0];
  }
  
  const ratio = (targetStation - fromStation) / totalDistance;
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  
  const startCoord = coords[0];
  const endCoord = coords[coords.length - 1];
  
  const interpolatedLng = startCoord[0] + (endCoord[0] - startCoord[0]) * clampedRatio;
  const interpolatedLat = startCoord[1] + (endCoord[1] - startCoord[1]) * clampedRatio;

  return [interpolatedLng, interpolatedLat];
};

// Slice a station-sorted wheelpath into a coord polyline covering [from,to].
// Returns [[lng,lat], ...] ordered from→to. Uncovered ends use the provided
// fallback coords (typically seg.geom's endpoints) so first/last segments of
// a session still get wheelpath curvature for the covered middle.
// Returns null only if the segment has no overlap with the wheelpath.
const sliceWheelpathByStation = (sortedWheelpath, fromStation, toStation, fromFallbackCoord, toFallbackCoord) => {
  if (!sortedWheelpath || sortedWheelpath.length < 2) return null;

  const lo = Math.min(fromStation, toStation);
  const hi = Math.max(fromStation, toStation);
  const first = sortedWheelpath[0].station;
  const last = sortedWheelpath[sortedWheelpath.length - 1].station;
  if (hi < first || lo > last) return null;

  const lowerBound = (value) => {
    let left = 0, right = sortedWheelpath.length;
    while (left < right) {
      const mid = (left + right) >> 1;
      if (sortedWheelpath[mid].station < value) left = mid + 1;
      else right = mid;
    }
    return left;
  };

  const interpAt = (station) => {
    const i = lowerBound(station);
    if (i === 0) {
      const p = sortedWheelpath[0];
      return [p.lng, p.lat];
    }
    if (i >= sortedWheelpath.length) {
      const p = sortedWheelpath[sortedWheelpath.length - 1];
      return [p.lng, p.lat];
    }
    const b = sortedWheelpath[i];
    if (b.station === station) return [b.lng, b.lat];
    const a = sortedWheelpath[i - 1];
    const span = b.station - a.station;
    const t = span === 0 ? 0 : (station - a.station) / span;
    return [a.lng + (b.lng - a.lng) * t, a.lat + (b.lat - a.lat) * t];
  };

  const ascending = fromStation <= toStation;
  const loFallback = ascending ? fromFallbackCoord : toFallbackCoord;
  const hiFallback = ascending ? toFallbackCoord : fromFallbackCoord;

  let startCoord;
  if (lo >= first) startCoord = interpAt(lo);
  else if (loFallback) startCoord = [loFallback[0], loFallback[1]];
  else return null;

  let endCoord;
  if (hi <= last) endCoord = interpAt(hi);
  else if (hiFallback) endCoord = [hiFallback[0], hiFallback[1]];
  else return null;

  // Interior samples: strictly inside the placed endpoints, skipping any
  // sample that coincides with startCoord/endCoord to avoid duplicates.
  const startIdx = (lo >= first)
    ? (() => {
        const i = lowerBound(lo);
        return (i < sortedWheelpath.length && sortedWheelpath[i].station === lo) ? i + 1 : i;
      })()
    : lowerBound(first);
  const endIdx = (hi <= last) ? lowerBound(hi) : sortedWheelpath.length;

  const interior = [];
  for (let i = startIdx; i < endIdx; i++) {
    interior.push([sortedWheelpath[i].lng, sortedWheelpath[i].lat]);
  }

  const result = [startCoord, ...interior, endCoord];
  return ascending ? result : result.slice().reverse();
};

// Iterative Douglas-Peucker simplification. Drops vertices whose perpendicular
// distance to the chord falls below epsilonMeters. Preserves curvature, thins
// straight runs. Uses a flat-earth meters approximation — fine at segment scale.
const simplifyPolyline = (coords, epsilonMeters) => {
  if (!coords || coords.length < 3 || epsilonMeters <= 0) return coords;

  const midLat = coords[Math.floor(coords.length / 2)][1];
  const mPerLat = 111132;
  const mPerLon = 111320 * Math.cos(midLat * Math.PI / 180);
  const sqEps = epsilonMeters * epsilonMeters;

  const sqPerpDist = (p, a, b) => {
    const axm = (b[0] - a[0]) * mPerLon;
    const aym = (b[1] - a[1]) * mPerLat;
    const pxm = (p[0] - a[0]) * mPerLon;
    const pym = (p[1] - a[1]) * mPerLat;
    const len2 = axm * axm + aym * aym;
    if (len2 === 0) return pxm * pxm + pym * pym;
    const cross = pxm * aym - pym * axm;
    return (cross * cross) / len2;
  };

  const keep = new Uint8Array(coords.length);
  keep[0] = 1;
  keep[coords.length - 1] = 1;

  const stack = [[0, coords.length - 1]];
  while (stack.length > 0) {
    const [s, e] = stack.pop();
    let maxSq = 0;
    let idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = sqPerpDist(coords[i], coords[s], coords[e]);
      if (d > maxSq) { maxSq = d; idx = i; }
    }
    if (idx !== -1 && maxSq > sqEps) {
      keep[idx] = 1;
      stack.push([s, idx]);
      stack.push([idx, e]);
    }
  }

  const result = [];
  for (let i = 0; i < coords.length; i++) {
    if (keep[i]) result.push(coords[i]);
  }
  return result;
};


  const getBumpColor = (magnitude) => {
  if (magnitude < 0.5) return [255, 179, 179, 200]; // Light Red
  if (magnitude < 1.0) return [255, 102, 102, 200]; // Medium Red
  return [255, 0, 0, 220];                     // Bright Red
};

// Orange-scale for Dips
const getDipColor = (magnitude) => {
  if (magnitude < 0.5) return [255, 213, 179, 200]; // Light Orange
  if (magnitude < 1.0) return [255, 177, 102, 200]; // Medium Orange
  return [255, 140, 0, 220];                     // Bright Orange
};

const parsePostGISPoint = (wkbHex) => {
  try {
    if (!wkbHex || typeof wkbHex !== 'string') return null;
    
    const hex = wkbHex.replace(/\s/g, '').toUpperCase();
    
    if (hex.length < 50) return null;
    
    // Parse WKB hex for POINT geometry
    // Skip header bytes (18 hex chars) and extract X,Y coordinates
    const xHex = hex.substring(18, 34);
    const yHex = hex.substring(34, 50);
    
    // Convert hex to float64 (little endian)
    const hexToFloat64 = (hexStr) => {
      const bytes = [];
      for (let i = 0; i < 16; i += 2) {
        bytes.push(parseInt(hexStr.substr(i, 2), 16));
      }
      
      const buffer = new ArrayBuffer(8);
      const view = new DataView(buffer);
      bytes.forEach((byte, i) => view.setUint8(i, byte));
      
      return view.getFloat64(0, true);
    };
    
    let lng = hexToFloat64(xHex);
    let lat = hexToFloat64(yHex);
    
    // Fix coordinate signs if needed
    // Iowa coordinates should be around:
    // Longitude: -93 to -94 (negative for western hemisphere)
    // Latitude: 41 to 43 (positive for northern hemisphere)
    
    // If longitude is positive and around 93, make it negative
    if (lng > 0 && lng > 90 && lng < 100) {
      lng = -lng;
    }
    
    // Validate coordinates
    if (isNaN(lng) || isNaN(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
      console.warn('Invalid coordinates:', { lng, lat });
      return null;
    }
    
    return { lat, lng };
  } catch (error) {
    console.error('Error parsing PostGIS point:', error);
    return null;
  }
};

// Helper to extract station from filename (e.g., "0005+00.jpg" -> "0005+00")
const extractStation = (filename) => {
  const match = filename.match(/(\d{4}\+\d{2})/);
  return match ? match[1] : filename.replace('.jpg', '');

  
};

  const formatStation = (station) => {
    if (station === null || station === undefined) return 'N/A';
    const hundreds = Math.floor(station / 100);
    const remainder = station % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  };

const handleDrawerAnimationEnd = useCallback(() => {
  if (!isPlaybackActive) {
    setIsDrawerAnimating(false);
  }
}, [isPlaybackActive]);

const handleStartKmlScrub = useCallback(() => {
  if (!segments || segments.length === 0) return;
  
  if (kmlScrubStation === null) {
    const allStations = segments.map(s => s.from_station_ft).concat(segments.map(s => s.to_station_ft));
    const startStation = isReversed ? Math.max(...allStations) : Math.min(...allStations);
    setKmlScrubStation(startStation);
  }

  setIsPlaybackActive(false);
  setIsDataTableActive(false);
  setIsKmlScrubActive(true);
  setIsKmlScrubPlaying(false);
  setIsKmlScrubCameraLocked(true);
}, [segments, kmlScrubStation, isReversed]);

const handleToggleDataTable = useCallback(() => {
  setIsDataTableActive(prev => {
    if (!prev) {
      setIsPlaybackActive(false);
      setIsKmlScrubActive(false);
    }
    return !prev;
  });
}, []);

// Rebuild segment polylines from the IRI wheelpath so short, curved segments
// (e.g. off-ramps) follow the road instead of drawing as begin→end chords.
// Falls back to seg.geom when no wheelpath sample covers the segment's range.
const reconstructedSegmentPaths = useMemo(() => {
  const wp = iriProfileData?.leftWheelpath?.length
    ? iriProfileData.leftWheelpath
    : iriProfileData?.rightWheelpath?.length
      ? iriProfileData.rightWheelpath
      : null;
  if (!wp || !segments || segments.length === 0) {
    return null;
  }

  // kmlProcessor emits real station feet for wheelpath samples. Do not remap
  // those values here; reversed runs are already represented in the station data.
  const sorted = [...wp].sort((a, b) => a.station - b.station);

  const map = new Map();
  for (const seg of segments) {
    const geomCoords = parseGeometry(seg.geom)?.coordinates;
    const fromFallback = geomCoords?.[0];
    const toFallback = geomCoords?.[geomCoords.length - 1];
    const coords = sliceWheelpathByStation(
      sorted,
      seg.from_station_ft,
      seg.to_station_ft,
      fromFallback,
      toFallback
    );
    if (coords && coords.length >= 2) {
      const orderedCoords = isReversed ? coords.slice().reverse() : coords;
      const simplified = simplifyPolyline(orderedCoords, 0.5);
      map.set(seg.segment_id, simplified);
    }
  }
  return map;
}, [segments, iriProfileData, parseGeometry, isReversed]);

// Add this function near the top of your component, after your other helper functions
const findPositionAtStation = useCallback((targetStation, segmentsData) => {
    if (!segmentsData || segmentsData.length === 0) return null;

    // Robust find: Handle cases where from > to or from < to
    const segment = segmentsData.find(s => {
      const min = Math.min(s.from_station_ft, s.to_station_ft);
      const max = Math.max(s.from_station_ft, s.to_station_ft);
      return targetStation >= min && targetStation <= max;
    });

    if (!segment) return null;

    // Prefer the reconstructed wheelpath-aligned polyline so the marker sits
    // on the segment's drawn path (not on the 2-pt geom chord).
    const reconstructed = reconstructedSegmentPaths?.get(segment.segment_id);
    const coords = reconstructed && reconstructed.length >= 2
      ? reconstructed
      : parseGeometry(segment.geom)?.coordinates;
    if (!coords || coords.length === 0) return null;
    
    // 1. Calculate total length of geometry in meters
    // (We must walk the line to handle curves correctly)
    const getDistMeters = (c1, c2) => {
      const R = 6371e3; 
      const φ1 = c1[1] * Math.PI/180;
      const φ2 = c2[1] * Math.PI/180;
      const Δφ = (c2[1]-c1[1]) * Math.PI/180;
      const Δλ = (c2[0]-c1[0]) * Math.PI/180;
      const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    };

    let totalGeomLength = 0;
    const dists = []; 
    
    for(let i=0; i<coords.length-1; i++) {
        const d = getDistMeters(coords[i], coords[i+1]);
        dists.push({ start: totalGeomLength, end: totalGeomLength + d, idx: i });
        totalGeomLength += d;
    }
    
    // 2. Calculate ratio
    // Important: Does the geometry go From->To or To->From?
    // We assume geometry is drawn in Driving Direction.
    // If Reverse Stationing (100->90), geometry[0] is 100, geometry[end] is 90.
    
    const startStation = isReversed ? Math.max(segment.from_station_ft, segment.to_station_ft) : Math.min(segment.from_station_ft, segment.to_station_ft);
    const endStation = isReversed ? Math.min(segment.from_station_ft, segment.to_station_ft) : Math.max(segment.from_station_ft, segment.to_station_ft);
    
    const totalStationRange = Math.abs(endStation - startStation);
    const distancefromStart = Math.abs(targetStation - startStation);
    
    const ratio = totalStationRange > 0 ? distancefromStart / totalStationRange : 0;
    const targetDistMeters = ratio * totalGeomLength;
    
    // 3. Interpolate
    const subSeg = dists.find(d => targetDistMeters >= d.start && targetDistMeters <= d.end) || dists[dists.length-1];
    if (!subSeg) return [coords[0][0], coords[0][1], 0];

    const subRatio = (targetDistMeters - subSeg.start) / (subSeg.end - subSeg.start);
    const p1 = coords[subSeg.idx];
    const p2 = coords[subSeg.idx + 1];
    
    const lng = p1[0] + (p2[0] - p1[0]) * subRatio;
    const lat = p1[1] + (p2[1] - p1[1]) * subRatio;
    
    return [lng, lat, 0];

  }, [parseGeometry, isReversed, reconstructedSegmentPaths]);

// Create KML scrub marker layer (red cylinder with bullseye icon on top)
const kmlScrubMarkerLayer = useMemo(() => {
  if (!isKmlScrubActive || kmlScrubStation === null || !segments) return [];
  
  const position = findPositionAtStation(kmlScrubStation, segments);
  if (!position) return [];

  const CYLINDER_HEIGHT = 50; // 50 meters tall

  const bullseyeIcon = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="red" stroke="white" stroke-width="3"/><circle cx="50" cy="50" r="30" fill="red"/><circle cx="50" cy="50" r="20" fill="white"/></svg>');

  return [
    // Red cylinder
    new ColumnLayer({
      id: 'kml-scrub-cylinder',
      data: [{ position, station: kmlScrubStation }],
      getPosition: d => d.position,
      getElevation: CYLINDER_HEIGHT,
      getFillColor: [255, 0, 0, 220], // Red
      radius: 0.5, // Skinny cylinder - 0.5 meters radius
      radiusUnits: 'meters',
      extruded: true,
      diskResolution: 32,
      pickable: false,
      material: {
        ambient: 0.8,
        diffuse: 0.4,
        shininess: 32,
        specularColor: [255, 150, 150]
      },
      parameters: {
        depthTest: false,
      },
    }),
    // Bullseye icon on top of cylinder
    new IconLayer({
      id: 'kml-scrub-icon',
      data: [{ position: [...position.slice(0, 2), CYLINDER_HEIGHT], station: kmlScrubStation }],
      getPosition: d => d.position,
      getIcon: () => ({
        url: bullseyeIcon,
        width: 100,
        height: 100,
        anchorX: 50,
        anchorY: 50,
        mask: false
      }),
      getSize: 30,
      sizeUnits: 'pixels',
      billboard: true,
      pickable: false,
      parameters: {
        depthTest: false,
      },
    })
  ];
}, [isKmlScrubActive, kmlScrubStation, segments, findPositionAtStation]);

// Auto-orient map: Start (Left) -> End (Right)
useEffect(() => {
  // 1. Checks: Map must exist, segments must exist, and we shouldn't have done this yet
  if (!map || !segments || segments.length === 0 || hasOrientedRef.current) {
    return;
  }

  // 2. Find Start and End Geometry
  // Sort segments by station to ensure we get the true linear start/end
  const sortedSegments = [...segments].sort((a, b) => a.from_station_ft - b.from_station_ft);

  const firstSeg = sortedSegments[0]; // Lowest Station
  const lastSeg = sortedSegments[sortedSegments.length - 1]; // Highest Station

  // Parse geometries using your existing helper
  const firstGeom = parseGeometry(firstSeg.geom);
  const lastGeom = parseGeometry(lastSeg.geom);

  if (!firstGeom?.coordinates?.length || !lastGeom?.coordinates?.length) return;

  // Get precise coordinate points
  // In a standard sort, firstSeg is the start, lastSeg is the end.
  const lowStationCoord = firstGeom.coordinates[0]; 
  const highStationCoord = lastGeom.coordinates[lastGeom.coordinates.length - 1];

  let startCoord, endCoord;

  // 3. Adjust for Reverse Stationing
  // If reversed, the survey started at High Station and ended at Low Station.
  // We want the visual "Left" of the screen to be the "Start" of the drive.
  if (isReversed) {
    startCoord = highStationCoord;
    endCoord = lowStationCoord;
  } else {
    startCoord = lowStationCoord;
    endCoord = highStationCoord;
  }

  // 4. Calculate Bearing
  // We use the Google Maps Geometry library (which you already load in LIBRARIES)
  const startLatLng = new window.google.maps.LatLng(startCoord[1], startCoord[0]);
  const endLatLng = new window.google.maps.LatLng(endCoord[1], endCoord[0]);
  
  const bearing = window.google.maps.geometry.spherical.computeHeading(startLatLng, endLatLng);

  // 5. Calculate Target Heading (Rotation)
  // If bearing is 90 (East), we want East at 3 o'clock (Right). 
  // So 'Up' (12 o'clock) needs to be North (0). 90 - 90 = 0.
  // If bearing is 270 (West), we want West at 3 o'clock (Right).
  // So 'Up' needs to be South (180). 270 - 90 = 180.
  const targetHeading = bearing - 90;

  // 6. Apply Rotation and Fit Bounds
  map.setHeading(targetHeading);
  
  // Optional: You might want to re-fit bounds here to ensure the rotated view fits the screen
  if (bounds) {
     const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.min_lat, bounds.min_lon),
        new window.google.maps.LatLng(bounds.max_lat, bounds.max_lon)
      );
      map.fitBounds(googleBounds, 50); // 50px padding
  }

  // Mark as done so we don't override user interaction later
  hasOrientedRef.current = true;

}, [map, segments, bounds, parseGeometry, isReversed]); // Dependencies

useEffect(() => {
  if (!isKmlScrubCameraLocked || !isKmlScrubActive || !map || !segments) return;
  
  const position = findPositionAtStation(kmlScrubStation, segments);
  if (!position) return;

  const [lng, lat] = position;
  map.panTo({ lat, lng });
}, [kmlScrubStation, isKmlScrubCameraLocked, isKmlScrubActive, map, segments, findPositionAtStation]);

useEffect(() => {
  if (isPlaybackActive) {
    // Small delay to allow drawer to render in its initial position first
    // Then both animations start together
    setTimeout(() => {
      setIsDrawerAnimating(true);
    }, 50);
  }
}, [isPlaybackActive]);


useEffect(() => {
  setPhotoMarkers([]);
  setPhotoCatalogRequested(false);
  setPhotoCatalogLoaded(false);
  setPendingPlaybackStart(false);
  setIsPlaybackActive(false);
  setPhotoError(null);
  setPhotoLoading(false);
}, [sessionId, isProjectMap]);

useEffect(() => {
    let cancelled = false;

    const loadSessionPhotos = async () => {
      if (!photoCatalogRequested || photoCatalogLoaded || !sessionId || isProjectMap) return;

      setPhotoLoading(true);
      setPhotoError(null);

      try {
        // 1. Fetch Metadata ONLY (No signed URLs yet)
        const { data, error } = await supabase
          .from('session_files')
          .select('id, filename, storage_path, geom, file_size, created_at') // Select specific columns
          .eq('session_id', sessionId)
          .eq('file_type', 'image')
          .not('geom', 'is', null);

        if (error) throw error;

        if (data && data.length > 0) {
          // 2. Map to lightweight objects (No API calls here)
          const markers = data.map((photo) => {
            let coords = null;

            // Handle GeoJSON format
            if (photo.geom && typeof photo.geom === 'object') {
              if (photo.geom.type === 'Point' && photo.geom.coordinates) {
                const [lng, lat] = photo.geom.coordinates;
                coords = { lng, lat };
              }
            } else if (typeof photo.geom === 'string') {
               // Fallback for WKB string parsing
               coords = parsePostGISPoint(photo.geom);
            }

            if (!coords) return null;

            return {
              id: photo.id,
              position: [coords.lng, coords.lat, 0],
              filename: photo.filename,
              station: extractStation(photo.filename),
              url: null, // intentionally null
              thumbnailUrl: null, // intentionally null
              storage_path: photo.storage_path, // Keep this! We need it for the buffer.
              file_size: photo.file_size,
              created_at: photo.created_at,
            };
          }).filter(m => m !== null);

          // 3. Perform the Natural Sort
          const getFileNumber = (filename) => {
            const stationMatch = filename.match(/(\d{4}\+\d{2})/);
            if (stationMatch) {
              return parseInt(stationMatch[1].replace('+', ''), 10);
            }
            const fallbackMatch = filename.match(/(\d+\.?\d*)/);
            return fallbackMatch ? parseFloat(fallbackMatch[1]) : 0;
          };

          const naturallySortedMarkers = markers.sort((a, b) => {
            return getFileNumber(a.filename) - getFileNumber(b.filename);
          });

          if (cancelled) return;
          setPhotoMarkers(naturallySortedMarkers);
          if (pendingPlaybackStart && naturallySortedMarkers.length > 0) {
            setIsPlaybackActive(true);
          }
        } else if (!cancelled && pendingPlaybackStart) {
          setPhotoError('No photos available for this session.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading photos:', error);
        setPhotoError(error.message);
      } finally {
        if (cancelled) return;
        setPhotoCatalogLoaded(true);
        setPendingPlaybackStart(false);
        setPhotoLoading(false);
      }
    };

    loadSessionPhotos();
    return () => {
      cancelled = true;
    };
  }, [photoCatalogRequested, photoCatalogLoaded, pendingPlaybackStart, sessionId, isProjectMap]);

const photoBillboardLayers = useMemo(() => {
  // Always return an empty array. The photos will not be drawn.
  return [];
}, []);


const playbackIconLayer = useMemo(() => {
  if (!isPlaybackActive || photoMarkers.length === 0) return [];
  
  const currentPhoto = photoMarkers[playbackIndex];
  if (!currentPhoto) return [];

  const bullseyeIcon = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="red" stroke="white" stroke-width="3"/><circle cx="50" cy="50" r="30" fill="red"/><circle cx="50" cy="50" r="20" fill="white"/></svg>');

  return [
    new IconLayer({
      id: 'playback-icon',
      data: [currentPhoto],
      getPosition: d => d.position,
      getIcon: d => ({
        url: bullseyeIcon,
        width: 100,
        height: 100,
        anchorX: 50,
        anchorY: 50,
        mask: false
      }),
      getSize: 30,
      sizeUnits: 'pixels',
      billboard: true,
      parameters: {
        depthTest: false,
      }
    })
  ];
}, [isPlaybackActive, photoMarkers, playbackIndex]);



  const DEFAULT_IRI_RANGES = [
  {"label": "Excellent", "max": 40, "description": "< 40 in/mi"},
  {"label": "Very Good", "max": 60, "description": "40-60 in/mi"},
  {"label": "Satisfactory", "max": 90, "description": "60-90 in/mi"},
  {"label": "Poor", "max": 140, "description": "90-140 in/mi"},
  {"label": "Very Poor", "max": 9999, "description": "> 140 in/mi"}
];

const COLOR_PALETTES = {
  2: ['#2EFF2E', '#D50000'],
  3: ['#2EFF2E', '#FFEB3B', '#D50000'],
  4: ['#2EFF2E', '#00A300', '#FFEB3B', '#D50000'],
  5: ['#2EFF2E', '#00A300', '#FFEB3B', '#FFA500', '#D50000'],
};

const IRI_COLOR_SCALE = useMemo(() => {
  const customRanges = projectConfig?.iri_color_scale;

  const isValidScale = Array.isArray(customRanges) && 
    customRanges.length >= 2 && 
    customRanges.length <= 5;

  const sourceRanges = isValidScale ? customRanges : DEFAULT_IRI_RANGES;
  const colors = COLOR_PALETTES[sourceRanges.length] || COLOR_PALETTES[5];

  const finalRanges = sourceRanges.map((range, index) => ({
    ...range,
    color: colors[index]
  }));

  return {
    ranges: finalRanges,
    exceptionColor: '#00FFFF',
    defaultColor: '#A0A0A0'
  };
}, [projectConfig]);

const alrDefects = useMemo(() => {
  const threshold = projectConfig?.iri_threshold_type_b ?? 0;

  return (events || [])
    .filter(e => {
      const isLR = (e.event_type || '').toUpperCase() === 'LR';
      const aboveThreshold = (e.magnitude_in ?? 0) > threshold;

      const wheelpathMatch =
        (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
        (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
        !e.wheelpath;

      return isLR && e.geom && aboveThreshold && wheelpathMatch;
    })
    .map(e => ({
      eventId: e.event_id,
      fromStation: e.from_station_ft,
      toStation: e.to_station_ft,
      magnitude: e.magnitude_in,
      wheelpath: e.wheelpath,
      ...e
    }))
    .sort((a, b) => a.fromStation - b.fromStation);
}, [events, projectConfig, wheelpathFilter]); // Added wheelpathFilter to dependencies

const surfaceEvents = useMemo(() => {
    return (events || [])
      .filter(e => {
        const type = (e.event_type || '').toUpperCase();
        return type === 'BUMP' || type === 'DIP';
      })
      .map(e => ({
        id: e.event_id,
        eventType: (e.event_type || '').toUpperCase() === 'BUMP' ? 'Bump' : 'Dip', // Normalize for chart label
        wheelpath: e.wheelpath, // Expects 'Left' or 'Right'
        distance: e.from_station_ft, // Chart expects 'distance' for start
        endStation: e.to_station_ft,
        peak_station: e.peak_station_ft,
        magnitude: e.magnitude_in,
        height: e.magnitude_in // Support both keys just in case
      }));
  }, [events]);


const ignoredSegments = useMemo(() => {
  return (segments || []).filter(s => s.exception);
}, [segments]);

const handleSelectAlrFromChart = useCallback((alrId) => {
    const alrEvent = events.find(e => e.event_id === alrId);
    if (alrEvent) {
      setSelectedALR(alrEvent);
      
    }
  }, [events]);


  const getSegmentColor = useCallback((segment) => {
    if (segment.exception) {
      return IRI_COLOR_SCALE.exceptionColor;
    }
    if (isProjectMap && segment.sessionInfo) {
      return sessionColorMap.get(segment.sessionInfo.session_id) || IRI_COLOR_SCALE.defaultColor;
    }
    const iri = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;
    if (iri <= 0) {
      return IRI_COLOR_SCALE.defaultColor;
    }
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) {
        return range.color;
      }
    }
    return IRI_COLOR_SCALE.defaultColor;
  }, [IRI_COLOR_SCALE, isProjectMap, sessionColorMap]);

  const EVENT_TYPES = useMemo(() => ({
    BUMP: { id: 'bumps', label: 'Bumps', color: '#FF3333', icon: '▲' },
    DIP: { id: 'dips', label: 'Dips', color: '#FF9500', icon: '▼' },
    LR: { id: 'lr', label: 'ALRs', color: '#AA00FF', icon: '●' },
    KML: { id: 'kml', label: '3D KML', color: '#8B5CF6', description: '3D visualization of pavement profile' }
  }), []);

useEffect(() => {
  if (!map) return;
  
  // Suppress WebGL MRT errors caused by deck.gl + Google Maps context sharing
  const suppressDrawBuffersError = () => {
    const gl = map.getDiv().querySelector('canvas')?.getContext('webgl2') || 
               map.getDiv().querySelector('canvas')?.getContext('webgl');
    if (gl && gl.drawBuffers) {
      const originalDrawBuffers = gl.drawBuffers.bind(gl);
      gl.drawBuffers = function(buffers) {
        try {
          return originalDrawBuffers(buffers);
        } catch (e) {
          // Silently ignore MRT errors - they're cosmetic
        }
      };
    }
  };
  
  // Small delay to ensure Google Maps canvas is ready
  setTimeout(suppressDrawBuffersError, 100);

  const overlay = new GoogleMapsOverlay({
    layers: [],
    interleaved: false,  // Use overlay mode to avoid context conflicts
    useDevicePixels: true,  // Render at native device resolution for crisp visuals
  });

  overlay.setMap(map);
  deckOverlayRef.current = overlay;

  return () => {
    if (deckOverlayRef.current) {
      deckOverlayRef.current.setMap(null);
      deckOverlayRef.current.finalize();
      deckOverlayRef.current = null;
    }
  };
}, [map]);

const kmlLayers = useMemo(() => {
    const layers = [];
    const Z_SCALE = iriAltitudeScale || 5;

    // ---------------------------------------------------------
    // 1. THRESHOLD LINE (Always Visible if KML Layer is On)
    // ---------------------------------------------------------
    const hasIriData = iriProfileData && (
      (iriProfileData.leftWheelpath && iriProfileData.leftWheelpath.length > 0) ||
      (iriProfileData.rightWheelpath && iriProfileData.rightWheelpath.length > 0)
    );

    if (layerVisibility.KML && hasIriData && projectConfig?.iri_threshold_type_b) {
      const thresholdHeight = projectConfig.iri_threshold_type_b * Z_SCALE;
      const refData = (iriProfileData.leftWheelpath && iriProfileData.leftWheelpath.length > 0) 
        ? iriProfileData.leftWheelpath 
        : iriProfileData.rightWheelpath;
      
      if (refData && refData.length > 0) {
        const thresholdPath = refData.map(p => [p.lng, p.lat, thresholdHeight]);
        
        layers.push(
          new PathLayer({
            id: 'kml-iri-threshold-line',
            data: [{ path: thresholdPath }],
            getPath: d => d.path,
            getColor: [255, 0, 255, 200],
            getWidth: 0.5,
            widthUnits: 'meters',
            billboard: true,
            getDashArray: [8, 4],
            extensions: [new PathStyleExtension({ dash: true })]
          })
        );
      }
    }

    // ---------------------------------------------------------
    // 2. IRI PATHS (Left=Green, Right=Blue, >Threshold=Red)
    // ---------------------------------------------------------
    if (hasIriData && layerVisibility.KML) {
      
      const thresholdVal = projectConfig?.iri_threshold_type_b || 150;

      const generatePathSegments = (points, wheelpathType) => {
        const segments = [];
        const baseColor = wheelpathType === 'left' ? [0, 255, 0] : [0, 100, 255];
        const alertColor = [255, 0, 0];

        for (let i = 0; i < points.length - 1; i++) {
          const curr = points[i];
          const next = points[i + 1];
          if (curr.iri === null || next.iri === null) continue;

          const dist = Math.abs(next.station - curr.station);
          if (dist > 50) continue; 

          const isAboveThreshold = curr.iri > thresholdVal || next.iri > thresholdVal;

          segments.push({
            path: [
              [curr.lng, curr.lat, curr.iri * Z_SCALE], 
              [next.lng, next.lat, next.iri * Z_SCALE]
            ],
            color: isAboveThreshold ? alertColor : baseColor,
            iri: curr.iri,
            station: curr.stationLabel
          });
        }
        return segments;
      };

      const leftSegments = wheelpathFilter.LEFT && iriProfileData.leftWheelpath 
        ? generatePathSegments(iriProfileData.leftWheelpath, 'left') : [];
        
      const rightSegments = wheelpathFilter.RIGHT && iriProfileData.rightWheelpath 
        ? generatePathSegments(iriProfileData.rightWheelpath, 'right') : [];
        
      const allSegments = [...leftSegments, ...rightSegments];

      if (allSegments.length > 0) {
        layers.push(new PathLayer({
          id: 'kml-iri-3d-path',
          data: allSegments,
          pickable: true,
          widthScale: 1,
          widthMinPixels: 1,
          getPath: d => d.path,
          getColor: d => d.color,
          getWidth: 0.6, 
          widthUnits: 'meters',
          billboard: true, 
          capRounded: true,
          jointRounded: true,
          parameters: { depthTest: true }
        }));
      }
    }

    return layers;
  }, [
    iriProfileData, 
    layerVisibility.KML, 
    wheelpathFilter, 
    iriAltitudeScale, 
    projectConfig
  ]);


// Start/End marker layers - ALWAYS visible (independent of KML toggle)
const runMarkerLayers = useMemo(() => {
  if (!kmlMarkers?.start && !kmlMarkers?.end) return [];

  const layers = [];
  
  // Smaller when zoomed in, larger when zoomed out
  const getSpireScale = (zoom) => {
    if (zoom >= 20) return 0.5;
    if (zoom >= 19) return 1;
    if (zoom >= 18) return 1.5;
    if (zoom >= 17) return 2;
    if (zoom >= 16) return 3;
    if (zoom >= 15) return 4;
    if (zoom >= 14) return 6;
    if (zoom >= 13) return 10;
    if (zoom >= 12) return 15;
    return 25;
  };
  
  const scale = getSpireScale(currentZoom);
  const totalHeight = 50 * scale;
  const radius = 3 * scale;

  // START MARKER - Single green column with flag
  if (kmlMarkers?.start) {
    const { lng, lat } = kmlMarkers.start;
    
    layers.push(new ColumnLayer({
      id: 'run-start-col',
      data: [{ position: [lng, lat, 0] }],
      getPosition: d => d.position,
      getElevation: totalHeight,
      getFillColor: [0, 255, 0, 220],
      radius: radius,
      radiusUnits: 'meters',
      extruded: true,
      pickable: false
    }));

const startFlag = 'data:image/svg+xml;base64,' + btoa('<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M20 10 L20 90 M20 10 L80 30 L80 50 L20 30 Z" fill="#00ff00" stroke="white" stroke-width="4"/></svg>');

    layers.push(new IconLayer({
      id: 'run-start-flag',
      data: [{ position: [lng, lat, totalHeight] }],
      getPosition: d => d.position,
      getIcon: () => ({ 
        url: startFlag, 
        width: 100, 
        height: 100, 
        anchorX: 20, 
        anchorY: 50, 
        mask: false 
      }),
      getSize: 15 * scale,
      sizeUnits: 'meters',
      billboard: true,
      pickable: false,
      parameters: { depthTest: false }
    }));
  }

  // END MARKER - Stacked red and white layers with checkered flag
  if (kmlMarkers?.end) {
    const { lng, lat } = kmlMarkers.end;
    const numStripes = 6;
    const stripeHeight = totalHeight / numStripes;
    
    for (let i = 0; i < numStripes; i++) {
      const isRed = i % 2 === 0;
      layers.push(new ColumnLayer({
        id: `run-end-col-${i}`,
        data: [{ position: [lng, lat, i * stripeHeight] }],
        getPosition: d => d.position,
        getElevation: stripeHeight,
        getFillColor: isRed ? [255, 0, 0, 220] : [255, 255, 255, 220],
        radius: radius,
        radiusUnits: 'meters',
        extruded: true,
        pickable: false
      }));
    }

const endFlag = 'data:image/svg+xml;base64,' + btoa('<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><rect x="20" y="10" width="60" height="40" fill="black"/><rect x="20" y="10" width="15" height="10" fill="white"/><rect x="50" y="10" width="15" height="10" fill="white"/><rect x="35" y="20" width="15" height="10" fill="white"/><rect x="65" y="20" width="15" height="10" fill="white"/><rect x="20" y="30" width="15" height="10" fill="white"/><rect x="50" y="30" width="15" height="10" fill="white"/><rect x="35" y="40" width="15" height="10" fill="white"/><rect x="65" y="40" width="15" height="10" fill="white"/><path d="M20 10 L20 90" stroke="white" stroke-width="4"/></svg>');
    layers.push(new IconLayer({
      id: 'run-end-flag',
      data: [{ position: [lng, lat, totalHeight] }],
      getPosition: d => d.position,
      getIcon: () => ({ 
        url: endFlag, 
        width: 100, 
        height: 100, 
        anchorX: 20, 
        anchorY: 30, 
        mask: false 
      }),
      getSize: 15 * scale,
      sizeUnits: 'meters',
      billboard: true,
      pickable: false,
      parameters: { depthTest: false }
    }));
  }
  
  return layers;
}, [kmlMarkers, currentZoom]);

const dipLayers = useMemo(() => {
  return buildLollipopLayers({
    events, layerVisibility, wheelpathFilter, selectedBumpDip,
    hoveredEventId, parseGeometry, interpolatePositionAtStation, formatStation,
    isProjectMap, setHoveredEventId, setSelectedBumpDip, setSelectedALR, setSelectedSegment, deckClickRef,
    type: 'DIP', idPrefix: 'dip', color: [255, 140, 0],  hoverColor: [255, 170, 50],
  });
}, [events, layerVisibility.DIP, parseGeometry, selectedBumpDip, hoveredEventId, wheelpathFilter, isProjectMap, formatStation]);

const bumpLayers = useMemo(() => {
  return buildLollipopLayers({
    events, layerVisibility, wheelpathFilter, selectedBumpDip,
    hoveredEventId, parseGeometry, interpolatePositionAtStation, formatStation,
    isProjectMap, setHoveredEventId, setSelectedBumpDip, setSelectedALR, setSelectedSegment, deckClickRef,
    type: 'BUMP', idPrefix: 'bump', color: [255, 0, 0], hoverColor: [255, 80, 80],
  });
}, [events, layerVisibility.BUMP, parseGeometry, selectedBumpDip, hoveredEventId, wheelpathFilter, isProjectMap, formatStation]);


  const filteredALREvents = useMemo(() => {
  if (!layerVisibility.LR) return [];
  
  const threshold = projectConfig?.iri_threshold_type_b ?? 0;
  
  return (events || [])
    .filter(e =>
      (e.event_type || '').toUpperCase() === 'LR' &&
      e.geom &&
      (e.magnitude_in ?? 0) > threshold
    )
    .sort((a, b) => a.from_station_ft - b.from_station_ft); // Sort by station for logical navigation
}, [events, layerVisibility.LR, projectConfig]);

const filteredSegments = useMemo(() => {
  if (!layerVisibility.SEGMENTS && legendIriFilter !== 'exception') return [];

  return (segments || [])
    .filter(seg => {
      if (legendIriFilter === null) return !seg.exception;
      if (legendIriFilter === 'exception') return seg.exception;
      if (seg.exception) return false;
      const iri = seg.iri_avg_in_per_mi || seg.iri_in_per_mi || 0;
      const ranges = IRI_COLOR_SCALE.ranges;
      const prevMax = legendIriFilter === 0 ? 0 : ranges[legendIriFilter - 1].max;
      return iri > prevMax && iri <= ranges[legendIriFilter].max;
    })
    .sort((a, b) => a.from_station_ft - b.from_station_ft);
}, [segments, layerVisibility.SEGMENTS, legendIriFilter, IRI_COLOR_SCALE]);


const selectedSegmentALRCount = useMemo(() => {
    if (!selectedSegment || !events) return 0;
    
    return events.filter(e => 
      (e.event_type === 'LR') && 
      (e.from_station_ft >= selectedSegment.from_station_ft) &&
      (e.to_station_ft <= selectedSegment.to_station_ft)
    ).length;
  }, [selectedSegment, events]);


// Same wheelpath-based reconstruction as segments, but for ALR (LR) events.
// Each ALR's geom was originally captured from its own wheel track (L or R),
// so left vs right ALRs sit offset from centerline. Pick the matching
// wheelpath per event to preserve that distinction.
const reconstructedALRPaths = useMemo(() => {
  const leftWp = iriProfileData?.leftWheelpath?.length ? iriProfileData.leftWheelpath : null;
  const rightWp = iriProfileData?.rightWheelpath?.length ? iriProfileData.rightWheelpath : null;
  if ((!leftWp && !rightWp) || !events || events.length === 0) return null;

  const sortedLeft = leftWp ? [...leftWp].sort((a, b) => a.station - b.station) : null;
  const sortedRight = rightWp ? [...rightWp].sort((a, b) => a.station - b.station) : null;

  const map = new Map();
  for (const ev of events) {
    if ((ev.event_type || '').toUpperCase() !== 'LR' || !ev.geom) continue;

    const preferred = ev.wheelpath === 'Right' ? sortedRight : sortedLeft;
    const fallbackWp = ev.wheelpath === 'Right' ? sortedLeft : sortedRight;
    const wp = preferred || fallbackWp;
    if (!wp) continue;

    const geomCoords = parseGeometry(ev.geom)?.coordinates;
    const fromFallback = geomCoords?.[0];
    const toFallback = geomCoords?.[geomCoords.length - 1];
    const coords = sliceWheelpathByStation(
      wp,
      ev.from_station_ft,
      ev.to_station_ft,
      fromFallback,
      toFallback
    );
    if (coords && coords.length >= 2) {
      map.set(ev.event_id, simplifyPolyline(coords, 0.5));
    }
  }
  return map;
}, [events, iriProfileData, parseGeometry]);

const alrLayers = useMemo(() => {
  if (!layerVisibility.LR) return { nonSelected: [], selectedGround: [], selectedElevated: [] };

  const threshold = projectConfig?.iri_threshold_type_b ?? 0;
  const ELEVATION_METERS = 3;
  const CURTAIN_SKIP = 2;
  const CURTAIN_WIDTH_PX = 2;

  const alrEvents = (events || []).filter(e => {
    const isLR = (e.event_type || '').toUpperCase() === 'LR';
    const aboveThreshold = (e.magnitude_in ?? 0) > threshold;
    
    const wheelpathMatch = 
      (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
      (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
      !e.wheelpath;
    
    return isLR && e.geom && aboveThreshold && wheelpathMatch;
  });

  const data = alrEvents
    .map(ev => {
      const reconstructed = reconstructedALRPaths?.get(ev.event_id);
      const coords = reconstructed && reconstructed.length >= 2
        ? reconstructed
        : (parseGeometry(ev.geom)?.coordinates || []);
      if (!coords.length) return null;

      const isSelected = !!(selectedALR && selectedALR.event_id === ev.event_id);
      const elevatedZ = isSelected ? ELEVATION_METERS : 0;

      const pathElevated = coords.map(([lng, lat]) => [lng, lat, elevatedZ]);
      const pathGround   = coords.map(([lng, lat]) => [lng, lat, 0]);

      const curtainSegments = isSelected
        ? pathElevated
            .map((p, i) => (i % CURTAIN_SKIP === 0 || i === pathElevated.length - 1)
              ? { s: p, t: [p[0], p[1], 0] }
              : null
            )
            .filter(Boolean)
        : [];

      return {
        ...ev,
        __isSelected: isSelected,
        __pathElevated: pathElevated,
        __pathGround: pathGround,
        __curtain: curtainSegments
      };
    })
    .filter(Boolean);

  if (!data.length) return { nonSelected: [], selectedGround: [], selectedElevated: [] };

  const opaque = rgba => {
    if (!Array.isArray(rgba)) return [0, 0, 0, 255];
    const [r = 0, g = 0, b = 0] = rgba;
    return [r, g, b, 255];
  };

  // CHANGED: Render ALL ALRs at ground level (no filtering)
  const allData = data;
  const selectedData = data.filter(d => d.__isSelected);

  const nonSelectedLayers = [];
  const selectedGroundLayers = [];
  const selectedElevatedLayers = [];

  // === ALL ALRS AT GROUND LEVEL (ALWAYS VISIBLE) ===
  if (allData.length > 0) {
    nonSelectedLayers.push(
      new PathLayer({
        id: 'alr-ground-trace-all',
        data: allData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 60],
        getWidth: 2,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );

    nonSelectedLayers.push(
      new PathLayer({
        id: 'alr-casing-all',
        data: allData,
        getPath: d => d.__pathGround, // Changed to ground path
        getColor: [0, 0, 0, 255],
        getWidth: 4.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );

    nonSelectedLayers.push(
      new PathLayer({
        id: 'alr-colored-all',
        data: allData,
        getPath: d => d.__pathGround, // Changed to ground path
        getColor: d => opaque(getALRColor(d.magnitude_in ?? 0)),
        getWidth: 2.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => { if (object) { setSelectedALR(object); setSelectedBumpDip(null); setSelectedSegment(null); } },
        parameters: { depthTest: false }
      })
    );
  }

  // === SELECTED GROUND ELEMENTS (shadow/trace) ===
  if (selectedData.length > 0) {
    selectedGroundLayers.push(
      new PathLayer({
        id: 'alr-ground-trace-selected',
        data: selectedData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 255],
        opacity: 0.2,
        getWidth: 3,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );

    selectedGroundLayers.push(
      new LineLayer({
        id: 'alr-curtain-extrusion',
        data: selectedData.flatMap(d => d.__curtain),
        getSourcePosition: d => d.s,
        getTargetPosition: d => d.t,
        getColor: [0, 0, 0, 255],        
        getWidth: CURTAIN_WIDTH_PX,
        widthUnits: 'pixels',
        pickable: false,
        billboard: true,
        parameters: { depthTest: false }
      })
    );
  }

  // === SELECTED ELEVATED ELEMENTS (the actual elevated ALR path) ===
  if (selectedData.length > 0) {
    selectedElevatedLayers.push(
      new PathLayer({
        id: 'alr-casing-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: [0, 0, 0, 255],
        getWidth: 5.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );

    selectedElevatedLayers.push(
      new PathLayer({
        id: 'alr-colored-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: d => opaque(getALRColor(d.magnitude_in ?? 0)),
        getWidth: 3,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => { if (object) { setSelectedALR(object); setSelectedBumpDip(null); setSelectedSegment(null); } },
        parameters: { depthTest: false }
      })
    );
  }

  return { nonSelected: nonSelectedLayers, selectedGround: selectedGroundLayers, selectedElevated: selectedElevatedLayers };
}, [events, layerVisibility.LR, parseGeometry, projectConfig, selectedALR, wheelpathFilter, reconstructedALRPaths]);

// IRI Roadway Layer - 3D columns showing IRI values along each wheelpath
const iriRoadwayLayers = useMemo(() => {
  if (!layerVisibility.IRI_ROADWAY || !iriProfileData) return [];
  
  const layers = [];
  const Z_SCALE = iriAltitudeScale || 0.5; // Reuse the existing scale control
  const threshold = projectConfig?.iri_threshold_type_b || 140;
  
  // Column settings
  const COLUMN_RADIUS = 0.4; // meters - width of each column
  const COLUMN_INTERVAL = 2; // Sample every Nth point to avoid overcrowding
  
  // Helper to get IRI color from the color scale
  const getIRIColorRGBA = (iri, alpha = 255) => {
    if (iri === null || iri === undefined) return [160, 160, 160, alpha];
    
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) {
        const hex = range.color;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b, alpha];
      }
    }
    return [160, 160, 160, alpha];
  };
  
  // Generate column data for a wheelpath
  const generateColumnData = (points, wheelpathType) => {
    return points
      .filter((_, i) => i % COLUMN_INTERVAL === 0) // Sample every Nth point
      .filter(p => p.iri !== null && p.iri !== undefined)
      .map(p => ({
        position: [p.lng, p.lat, 0],
        height: Math.max(p.iri * Z_SCALE, 0.5), // Minimum height for visibility
        color: getIRIColorRGBA(p.iri, 230),
        iri: p.iri,
        station: p.station,
        stationLabel: p.stationLabel,
        wheelpath: wheelpathType,
        isAboveThreshold: p.iri > threshold
      }));
  };
  
  // Generate data based on wheelpath filter
  const leftColumns = wheelpathFilter.LEFT && iriProfileData.leftWheelpath 
    ? generateColumnData(iriProfileData.leftWheelpath, 'Left') : [];
  const rightColumns = wheelpathFilter.RIGHT && iriProfileData.rightWheelpath 
    ? generateColumnData(iriProfileData.rightWheelpath, 'Right') : [];
  
  const allColumns = [...leftColumns, ...rightColumns];
  
  if (allColumns.length > 0) {
    // Ground trace / shadow for spatial reference
    layers.push(new ScatterplotLayer({
      id: 'iri-roadway-ground-dots',
      data: allColumns,
      getPosition: d => d.position,
      getFillColor: [0, 0, 0, 40],
      getRadius: COLUMN_RADIUS * 1.2,
      radiusUnits: 'meters',
      pickable: false,
      parameters: { depthTest: false }
    }));
    
    // 3D Columns
    layers.push(new ColumnLayer({
      id: 'iri-roadway-columns',
      data: allColumns,
      getPosition: d => d.position,
      getElevation: d => d.height,
      getFillColor: d => d.color,
      radius: COLUMN_RADIUS,
      radiusUnits: 'meters',
      diskResolution: 12,
      extruded: true,
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 150],
      material: {
        ambient: 0.6,
        diffuse: 0.5,
        shininess: 32,
        specularColor: [200, 200, 200]
      },
      parameters: { depthTest: true }
    }));
    
    // Threshold indicator caps - red tops on columns exceeding threshold
    const aboveThresholdColumns = allColumns.filter(c => c.isAboveThreshold);
    if (aboveThresholdColumns.length > 0) {
      layers.push(new ColumnLayer({
        id: 'iri-roadway-threshold-caps',
        data: aboveThresholdColumns,
        getPosition: d => [d.position[0], d.position[1], d.height - 2], // Sit on top
        getElevation: 3, // Small cap height
        getFillColor: [255, 0, 0, 255], // Bright red
        radius: COLUMN_RADIUS * 1.1, // Slightly wider
        radiusUnits: 'meters',
        diskResolution: 12,
        extruded: true,
        pickable: false,
        material: {
          ambient: 0.8,
          diffuse: 0.4,
          shininess: 64,
          specularColor: [255, 100, 100]
        },
        parameters: { depthTest: true }
      }));
    }
  }
  
  return layers;
}, [
  iriProfileData, 
  layerVisibility.IRI_ROADWAY, 
  wheelpathFilter, 
  projectConfig, 
  IRI_COLOR_SCALE, 
  formatStation,
  iriAltitudeScale  // Added this dependency to use the scale slider
]);




// Ghost marker — cached icon, managed outside main layers effect
const ghostMarkerIconUrl = useRef(null);
if (!ghostMarkerIconUrl.current) {
  ghostMarkerIconUrl.current = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="rgba(59,130,246,0.4)" stroke="white" stroke-width="2"/><circle cx="20" cy="20" r="6" fill="white"/></svg>');
}
const ghostLayerRef = useRef(null);

useEffect(() => {
  if (!deckOverlayRef.current) return;
  const currentLayers = deckOverlayRef.current.props?.layers || [];
  const withoutGhost = currentLayers.filter(l => l.id !== 'ghost-marker-icon');

  if (hoveredStation === null || !segments) {
    ghostLayerRef.current = null;
    deckOverlayRef.current.setProps({ layers: withoutGhost });
    return;
  }
  const position = findPositionAtStation(hoveredStation, segments);
  if (!position) {
    ghostLayerRef.current = null;
    deckOverlayRef.current.setProps({ layers: withoutGhost });
    return;
  }
  ghostLayerRef.current = new IconLayer({
    id: 'ghost-marker-icon',
    data: [{ position: [position[0], position[1], 0.5] }],
    getPosition: d => d.position,
    getIcon: () => ({ url: ghostMarkerIconUrl.current, width: 40, height: 40, anchorX: 20, anchorY: 20, mask: false }),
    getSize: 30,
    sizeUnits: 'pixels',
    pickable: false,
    billboard: true,
    parameters: { depthTest: false },
  });
  deckOverlayRef.current.setProps({ layers: [...withoutGhost, ghostLayerRef.current] });
}, [hoveredStation, segments, findPositionAtStation]);

const segmentLayers = useMemo(() => {
  if (!layerVisibility.SEGMENTS && !layerVisibility.EXCEPTIONS) return [];

  const ELEVATION_METERS = 5;
  const CURTAIN_SKIP = 2;
  const CURTAIN_WIDTH_PX = 2;
  // Split segments into normal and exceptions based on visibility toggles
  const normalSegments = (segments || []).filter(s => !s.exception && layerVisibility.SEGMENTS);
  const exceptionSegments = (segments || []).filter(s => s.exception && layerVisibility.EXCEPTIONS);

  // Combine visible segments, applying legend IRI filter
  const visibleSegments = [...normalSegments, ...exceptionSegments].filter(seg => {
    if (legendIriFilter === null) return true;
    if (legendIriFilter === 'exception') return seg.exception;
    if (seg.exception) return false;
    const iri = seg.iri_avg_in_per_mi || seg.iri_in_per_mi || 0;
    const ranges = IRI_COLOR_SCALE.ranges;
    const prevMax = legendIriFilter === 0 ? 0 : ranges[legendIriFilter - 1].max;
    return iri > prevMax && iri <= ranges[legendIriFilter].max;
  });

  const data = visibleSegments
    .map(seg => {
      const reconstructed = reconstructedSegmentPaths?.get(seg.segment_id);
      const coords = reconstructed && reconstructed.length >= 2
        ? reconstructed
        : (parseGeometry(seg.geom)?.coordinates || []);
      if (!coords.length) return null;

      const isSelected = !!(selectedSegment && selectedSegment.segment_id === seg.segment_id);
      const elevatedZ = isSelected ? ELEVATION_METERS : 0;

      const pathElevated = coords.map(([lng, lat]) => [lng, lat, elevatedZ]);
      const pathGround = coords.map(([lng, lat]) => [lng, lat, 0]);

      const curtainSegments = isSelected
        ? pathElevated
            .map((p, i) => (i % CURTAIN_SKIP === 0 || i === pathElevated.length - 1)
              ? { s: p, t: [p[0], p[1], 0] }
              : null
            )
            .filter(Boolean)
        : [];

      return {
        ...seg,
        __isSelected: isSelected,
        __pathElevated: pathElevated,
        __pathGround: pathGround,
        __curtain: curtainSegments
      };
    })
    .filter(Boolean);


  if (!data.length) return [];

  const nonSelectedData = data.filter(d => !d.__isSelected);
  const selectedData = data.filter(d => d.__isSelected);

  const layers = [];

  // === GROUND TRACES (single layer for all) ===
  if (nonSelectedData.length > 0) {
    layers.push(
      new PathLayer({
        id: 'segment-ground-trace-non-selected',
        data: nonSelectedData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 60],
        getWidth: 1.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );
  }

  // === RENDER EACH SEGMENT INDIVIDUALLY (casing + color) ===
  nonSelectedData.forEach((segment) => {
    // Black casing for this segment
    layers.push(
      new PathLayer({
        id: `segment-casing-${segment.segment_id}`,
        data: [segment],
        getPath: d => d.__pathElevated,
        getColor: [0, 0, 0, 255],
        getWidth: 6,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      })
    );

    // Colored fill for this segment
    layers.push(
      new PathLayer({
        id: `segment-colored-${segment.segment_id}`,
        data: [segment],
        getPath: d => d.__pathElevated,
        getColor: d => {
          const color = getSegmentColor(d);
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          return [r, g, b, 255];
        },
        getWidth: 4,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => {
          if (object) {
            deckClickRef.current = true;
            setSelectedSegment(object);
            setSelectedALR(null);
            setSelectedBumpDip(null);
          }
        },
        parameters: { depthTest: false }
      })
    );
  });

  // === SELECTED SEGMENT ON TOP (always rendered last) ===
if (selectedData.length > 0) {
  layers.push(
    new PathLayer({
      id: 'segment-ground-trace-selected',
      data: selectedData,
      getPath: d => d.__pathGround,
      getColor: [0, 0, 0, 255],
      opacity: 0.2,
      getWidth: 3,
      widthUnits: 'pixels',
      capRounded: true,
      jointRounded: true,
      billboard: true,
      pickable: false,
      parameters: { depthTest: false }
    })
  );

  layers.push(
    new LineLayer({
      id: 'segment-curtain-extrusion',
      data: selectedData.flatMap(d => d.__curtain),
      getSourcePosition: d => d.s,
      getTargetPosition: d => d.t,
      getColor: [0, 0, 0, 255],
      getWidth: CURTAIN_WIDTH_PX,
      widthUnits: 'pixels',
      pickable: false,
      billboard: true,
      parameters: { depthTest: false }
    })
  );

  // White stroke casing for selected segment
  layers.push(
    new PathLayer({
      id: 'segment-casing-selected',
      data: selectedData,
      getPath: d => d.__pathElevated,
      getColor: [255, 255, 255, 255], // Changed to white
      getWidth: 9, // Slightly increased
      widthUnits: 'pixels',
      capRounded: true,
      jointRounded: true,
      billboard: true,
      pickable: false,
      parameters: { depthTest: false }
    })
  );

  // Original segment color on top
  layers.push(
    new PathLayer({
      id: 'segment-colored-selected',
      data: selectedData,
      getPath: d => d.__pathElevated,
      getColor: d => {
        // Use original segment color instead of neon green
        const color = getSegmentColor(d);
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return [r, g, b, 255];
      },
      getWidth: 8,
      widthUnits: 'pixels',
      capRounded: true,
      jointRounded: true,
      billboard: true,
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 140],
      onClick: ({ object }) => {
        if (object) {
          deckClickRef.current = true;
          setSelectedSegment(object);
          setSelectedALR(null);
          setSelectedBumpDip(null);
        }
      },
      parameters: { depthTest: false }
    })
  );
}

	  return layers;
	}, [segments, layerVisibility.SEGMENTS, layerVisibility.EXCEPTIONS, parseGeometry, getSegmentColor, selectedSegment, isProjectMap, legendIriFilter, IRI_COLOR_SCALE, reconstructedSegmentPaths]);

	const stationMarkerLayers = useMemo(() => {
	  if (!layerVisibility.STATIONS || currentZoom < STATION_MARKER_MIN_ZOOM || !segments || segments.length === 0) {
	    return [];
	  }

	  const toFiniteStation = (value) => {
	    if (value === null || value === undefined || value === '') return null;
	    const parsed = Number(value);
	    return Number.isFinite(parsed) ? parsed : null;
	  };

	  const segmentStations = segments
	    .flatMap((segment) => [segment.from_station_ft, segment.to_station_ft])
	    .map(toFiniteStation)
	    .filter((station) => station !== null);

	  if (segmentStations.length === 0) return [];

	  const fallbackMin = Math.min(...segmentStations);
	  const fallbackMax = Math.max(...segmentStations);
	  const configuredStart = toFiniteStation(session?.analysis_start_station ?? session?.start_station_ft);
	  const configuredEnd = toFiniteStation(session?.analysis_end_station ?? session?.end_station_ft);
	  const rangeStart = configuredStart ?? fallbackMin;
	  const rangeEnd = configuredEnd ?? fallbackMax;
	  const rangeMin = Math.min(rangeStart, rangeEnd);
	  const rangeMax = Math.max(rangeStart, rangeEnd);
	  const rangeFt = rangeMax - rangeMin;

	  if (!Number.isFinite(rangeFt) || rangeFt <= 0) return [];

	  let intervalFt = currentZoom >= STATION_MARKER_DENSE_ZOOM
	    ? STATION_MARKER_DENSE_INTERVAL_FT
	    : STATION_MARKER_SPARSE_INTERVAL_FT;
	  const estimatedLabelCount = Math.floor(rangeFt / intervalFt) + 1;
	  if (estimatedLabelCount > STATION_MARKER_MAX_LABELS) {
	    intervalFt *= Math.ceil(estimatedLabelCount / STATION_MARKER_MAX_LABELS);
	  }

	  const stationsByKey = new Map();
	  const addStation = (station, isEndpoint = false) => {
	    if (!Number.isFinite(station)) return;
	    if (station < rangeMin - 0.1 || station > rangeMax + 0.1) return;
	    const rounded = Math.round(station * 10) / 10;
	    const key = rounded.toFixed(1);
	    stationsByKey.set(key, {
	      station: rounded,
	      isEndpoint: isEndpoint || stationsByKey.get(key)?.isEndpoint || false,
	    });
	  };

	  addStation(rangeStart, true);
	  addStation(rangeEnd, true);

	  const firstTick = Math.ceil(rangeMin / intervalFt) * intervalFt;
	  for (let station = firstTick; station <= rangeMax + 0.1; station += intervalFt) {
	    addStation(station);
	  }

	  const markerData = Array.from(stationsByKey.values())
	    .sort((a, b) => a.station - b.station)
	    .map((marker) => {
	      const position = findPositionAtStation(marker.station, segments);
	      if (!position) return null;

	      return {
	        ...marker,
	        id: `station-${marker.station.toFixed(1)}`,
	        position: [position[0], position[1], marker.isEndpoint ? 3 : 2],
	        label: formatStationMarkerLabel(marker.station),
	      };
	    })
	    .filter(Boolean);

	  if (markerData.length === 0) return [];

	  return [
	    new ScatterplotLayer({
	      id: 'station-marker-dots',
	      data: markerData,
	      getPosition: d => d.position,
	      getRadius: d => d.isEndpoint ? 5 : 3.5,
	      radiusUnits: 'pixels',
	      getFillColor: d => d.isEndpoint ? [17, 24, 39, 235] : [255, 255, 255, 235],
	      stroked: true,
	      getLineColor: d => d.isEndpoint ? [255, 255, 255, 245] : [17, 24, 39, 220],
	      getLineWidth: 1.5,
	      lineWidthUnits: 'pixels',
	      pickable: false,
	      billboard: true,
	      parameters: { depthTest: false },
	    }),
	    new TextLayer({
	      id: 'station-marker-labels',
	      data: markerData,
	      getPosition: d => d.position,
	      getText: d => d.label,
	      getSize: d => d.isEndpoint ? 13 : 12,
	      getColor: d => d.isEndpoint ? [255, 255, 255, 255] : [17, 24, 39, 255],
	      getTextAnchor: 'middle',
	      getAlignmentBaseline: 'center',
	      getPixelOffset: [0, -18],
	      billboard: true,
	      background: true,
	      getBackgroundColor: d => d.isEndpoint ? [17, 24, 39, 235] : [255, 255, 255, 235],
	      backgroundPadding: [5, 3],
	      backgroundBorderRadius: 4,
	      getBorderColor: [17, 24, 39, 160],
	      getBorderWidth: 1,
	      sizeUnits: 'pixels',
	      sizeMinPixels: 10,
	      sizeMaxPixels: 18,
	      pickable: false,
	      parameters: { depthTest: false },
	    }),
	  ];
	}, [layerVisibility.STATIONS, currentZoom, segments, session, findPositionAtStation]);
	
	
	  const iriDimensionData = useMemo(() => {
    const lines = [];
    const texts = [];
    segments.forEach(segment => {
      if (segment.exception) return;
      // Apply legend IRI filter
      if (legendIriFilter !== null && legendIriFilter !== 'exception') {
        const iri = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;
        const ranges = IRI_COLOR_SCALE.ranges;
        const prevMax = legendIriFilter === 0 ? 0 : ranges[legendIriFilter - 1].max;
        if (!(iri > prevMax && iri <= ranges[legendIriFilter].max)) return;
      }
      if (legendIriFilter === 'exception') return;
      const reconstructed = reconstructedSegmentPaths?.get(segment.segment_id);
      const coords = reconstructed && reconstructed.length >= 2
        ? reconstructed
        : parseGeometry(segment.geom)?.coordinates;
      if (!coords || coords.length < 2) return;

      const startCoord = coords[0];
      const endCoord = coords[coords.length - 1];
      const len = Math.sqrt(Math.pow(endCoord[0] - startCoord[0], 2) + Math.pow(endCoord[1] - startCoord[1], 2));
      if (len === 0) return;

      const perpendicular = [-(endCoord[1] - startCoord[1]) / len, (endCoord[0] - startCoord[0]) / len];
      const metersPerDegreeLat = 111132;
      const metersPerDegreeLon = 111320 * Math.cos(startCoord[1] * Math.PI / 180);
      const offsetX_lon = (perpendicular[0] * DIMENSION_OFFSET) / metersPerDegreeLon;
      const offsetY_lat = (perpendicular[1] * DIMENSION_OFFSET) / metersPerDegreeLat;

      
      const midpoint = [(startCoord[0] + endCoord[0]) / 2 + offsetX_lon, (startCoord[1] + endCoord[1]) / 2 + offsetY_lat, DIMENSION_ALTITUDE];

      const iriValue = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;

     

      texts.push({
        position: midpoint,
        text: iriValue.toFixed(1),
        color: IRI_TEXT_COLOR,

        backgroundColor: IRI_TEXT_BACKGROUND_COLOR,
        size: 13,
        backgroundPadding: [4, 20], // Increased padding
        borderRadius: 8, // Increased border radius
        textAnchor: 'middle',
        alignmentBaseline: 'center',
      });
    });
    return { lines, texts };
  }, [segments, parseGeometry, getSegmentColor, legendIriFilter, IRI_COLOR_SCALE, reconstructedSegmentPaths]);

  const iriDimensionLayers = useMemo(() => {
    if (!layerVisibility.SEGMENTS) return [];
    return [
      
      new TextLayer({
        id: 'iri-dimension-text',
        data: iriDimensionData.texts,
        visible: currentZoom >= IRI_TEXT_ZOOM_THRESHOLD, // Zoom-based visibility
        getPosition: d => d.position,
        getText: d => d.text,
        getColor: d => d.color,
        getSize: d => d.size,
        getTextAnchor: d => d.textAnchor,
        getAlignmentBaseline: d => d.alignmentBaseline,
        getPixelOffset: [0, -25], // Increased hover distance
        background: true,
        getBackgroundColor: d => d.backgroundColor,
        getBorderRadius: d => d.borderRadius,
        getPadding: d => d.padding,
        billboard: true, // ALWAYS FACE CAMERA
        sizeMinPixels: 12,
        sizeMaxPixels: 24,
        backgroundPadding: [6, 6], // Increased padding
        backgroundBorderRadius: 99, // Increased border radius
        getBorderColor: [0, 0, 0, 255],
        getBorderWidth: 1,
      })
    ];
  }, [iriDimensionData, layerVisibility.SEGMENTS, currentZoom]);

  useEffect(() => {
  if (!layerVisibility.LR && selectedALR) {
    setSelectedALR(null);
  }
}, [layerVisibility.LR, selectedALR]);

// Close segment info panel when SEGMENTS layer is turned off
useEffect(() => {
  if (!layerVisibility.SEGMENTS && selectedSegment) {
    setSelectedSegment(null);
  }
}, [layerVisibility.SEGMENTS, selectedSegment]);


// This effect updates the layers whenever they change
useEffect(() => {
  if (!deckOverlayRef.current) return;

	  const layers = [
	    ...kmlLayers,
	    ...runMarkerLayers,
	    ...segmentLayers,
	    ...stationMarkerLayers,
	    ...iriRoadwayLayers,
    ...alrLayers.nonSelected,
    ...alrLayers.selectedGround,
    ...dipLayers,
    ...alrLayers.selectedElevated,
    ...bumpLayers,
    ...photoBillboardLayers,
    ...playbackIconLayer,
    ...kmlScrubMarkerLayer,
    ...iriDimensionLayers,
    ...(ghostLayerRef.current ? [ghostLayerRef.current] : [])
  ];

  deckOverlayRef.current.setProps({ layers });
	}, [map, kmlLayers, iriDimensionLayers, photoBillboardLayers, alrLayers, segmentLayers, stationMarkerLayers, playbackIconLayer, dipLayers, bumpLayers, kmlScrubMarkerLayer, runMarkerLayers, iriRoadwayLayers]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    keyboardShortcuts: false,
    clickableIcons: false,
    mapTypeId: mapStyleMode === 'satellite' ? 'satellite' : 'roadmap',
    mapId: '24ee09cb1783e3c39c8fcb02',
    minZoom: 10,
    gestureHandling: 'greedy',
    rotateControl: false,
    scaleControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
  }), [mapStyleMode]);

  const createEventPolylines = useCallback((eventData) => {
    // This is the corrected filtering logic
    const filteredEvents = eventData.filter(event => {
    const eventType = (event.event_type || 'BUMP').toString().toUpperCase();
    
    // If the event is an ALR, BUMP, or DIP, EXCLUDE it.
    // Deck.gl is now responsible for drawing all three.
    if (eventType === 'LR' || eventType === 'BUMP' || eventType === 'DIP') {
      return false;
    }

    // Include any other event types you might have
    return true;
  });

    const polylines = [];
    const indicatorMarkers = [];

    // The rest of this function now only runs for Bumps and Dips
    filteredEvents.forEach((event) => {
      const geometry = parseGeometry(event.geom);
      if (!geometry || !geometry.coordinates || geometry.coordinates.length < 1) return;
      const path = geometry.coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));
      const eventType = (event.event_type || 'BUMP').toString().toUpperCase();
      const config = EVENT_TYPES[eventType] || EVENT_TYPES.BUMP;
      let strokeColor = config.color;
      if (isProjectMap && event.sessionInfo) {
        strokeColor = sessionColorMap.get(event.sessionInfo.session_id) || config.color;
      }
      polylines.push({
        id: `event-${event.event_id}`,
        path: path.length === 1 ? [path[0], path[0]] : path,
        options: { strokeColor, strokeWeight: 6, strokeOpacity: 0.9, clickable: true, zIndex: 200, },
        event,
        type: eventType,
      });
      if ((eventType === 'BUMP' || eventType === 'DIP') && path.length > 0) {
        indicatorMarkers.push({
          id: `indicator-${event.event_id}`,
          position: path[0],
          eventType,
          color: strokeColor,
          event,
        });
      }
    });
    setEventPolylines(polylines);
    setIndicators(indicatorMarkers);
  }, [EVENT_TYPES, parseGeometry, isProjectMap, sessionColorMap, projectConfig]);

  useEffect(() => {
  if (!isPlaying || !isPlaybackActive) {
    return; // Do nothing if paused or drawer is closed
  }

  // Check if at the end
  if (playbackIndex >= photoMarkers.length - 1) {
    setIsPlaying(false); // Stop playing at the last frame
    return;
  }

  const speeds = { 0.5: 2000, 1: 1000, 2: 500, 4: 250, 6: 166, 8: 125 };
  const delay = speeds[playbackSpeed] || 1000;

  const timerId = setTimeout(() => {
    setPlaybackIndex(prev => prev + 1);
  }, delay);

  // Cleanup function
  return () => clearTimeout(timerId);

}, [isPlaying, isPlaybackActive, playbackIndex, playbackSpeed, photoMarkers.length]);

useEffect(() => {
  if (!isCameraLocked || !isPlaybackActive || !map || photoMarkers.length === 0) {
    return;
  }
  
  const currentPhoto = photoMarkers[playbackIndex];
  if (!currentPhoto) return;

  const [lng, lat] = currentPhoto.position;
  
  // Use panTo for a smooth transition
  map.panTo({ lat, lng });

}, [playbackIndex, isCameraLocked, isPlaybackActive, map, photoMarkers]);

  useEffect(() => {
    const setInitialMapView = async () => {
      if (bounds && bounds.min_lon && bounds.max_lon && bounds.min_lat && bounds.max_lat) {
        const centerLat = (bounds.min_lat + bounds.max_lat) / 2;
        const centerLng = (bounds.min_lon + bounds.max_lon) / 2;
        setMapCenter({ lat: centerLat, lng: centerLng });
        setMapZoom(15);
      } else if (sessionId && !isProjectMap) {
        try {
          const { data, error } = await supabase
            .rpc('get_prototype_first_location', { session_id_input: sessionId });
          if (!error && data && data.length > 0 && data[0].lng && data[0].lat) {
            setMapCenter({ lat: data[0].lat, lng: data[0].lng });
            setMapZoom(16);
          }
        } catch (err) {
          console.error('Error getting first location:', err);
        }
      }
      setIsLoading(false);
    };
    setInitialMapView();
  }, [sessionId, bounds, isProjectMap]);

  useEffect(() => {
    if (events && events.length > 0) createEventPolylines(events);
  }, [events, createEventPolylines]);

  useEffect(() => {
    if (map && bounds && bounds.min_lon && bounds.max_lon && bounds.min_lat && bounds.max_lat) {
      const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.min_lat, bounds.min_lon),
        new window.google.maps.LatLng(bounds.max_lat, bounds.max_lon)
      );
      map.fitBounds(googleBounds);
    }
  }, [map, bounds]);

  // Pan map to selected ALR when it changes
useEffect(() => {
  if (!map || !selectedALR) return;

  const geometry = parseGeometry(selectedALR.geom);
  if (!geometry?.coordinates?.length) return;

  const coords = geometry.coordinates;
  const bounds = new window.google.maps.LatLngBounds();
  coords.forEach(([lng, lat]) => bounds.extend({ lat, lng }));

  // Only pan if the ALR is outside the current viewport
  const mapBounds = map.getBounds();
  if (mapBounds && !mapBounds.contains(bounds.getCenter())) {
    map.panTo(bounds.getCenter());
  }
}, [selectedALR, map, parseGeometry]);

  const onLoad = useCallback((map) => {
    setMap(map);
    mapRef.current = map;
    setCurrentZoom(map.getZoom()); // Set initial zoom state
    if (map.getTilt !== undefined) {
      map.setTilt(mapStatus.tilt);
      map.setHeading(mapStatus.heading);
      setCurrentHeading(mapStatus.heading);
    }

    map.addListener('dragstart', () => {
    setIsCameraLocked(false);
  });
  map.addListener('zoom_changed', () => {
    setIsCameraLocked(false);
  });
  map.addListener('heading_changed', () => {
    setCurrentHeading(map.getHeading?.() || 0);
  });
  }, [mapStatus.tilt, mapStatus.heading]);

  const onUnmount = useCallback(() => {
    setMap(null);
    mapRef.current = null;
  }, []);
  
  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      setCurrentZoom(mapRef.current.getZoom());
    }
  }, []);

  const getIRIRating = (iri) => {
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) return range.label;
    }
    return 'Unknown';
  };



  const handlePolylineClick = useCallback((e, polyline) => {
    if (!polyline.segment && !polyline.event) return;
    const position = e.latLng;

    if (polyline.type === 'SEGMENT' || polyline.type === 'EXCEPTION') {
      const segment = polyline.segment;
      const iri = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;
      setInfoWindow({
        position,
        content: `
          <div class="p-3 bg-gray-800 text-white rounded shadow-lg max-w-sm">
            <div class="font-bold text-lg mb-2" style="color: ${polyline.options.strokeColor}">
              ${segment.exception ? 'Exception Area' : 'Road Segment'}
            </div>
            ${isProjectMap && segment.sessionInfo ? `<div class="text-xs text-gray-400 mb-2">Session: ${segment.sessionInfo.name || segment.sessionInfo.filename || 'Unnamed'}</div>` : ''}
            <div class="space-y-1 text-sm">
              ${segment.exception ? `<div class="text-red-400 font-medium">IRI data ignored</div><div><span class="font-medium">Reason:</span> Equipment issue</div>` : `<div><span class="font-medium">IRI Average:</span> ${iri.toFixed(1)} in/mi</div><div><span class="font-medium">IRI Rating:</span> ${getIRIRating(iri)}</div>`}
              <div><span class="font-medium">Station:</span> ${formatStation(segment.from_station_ft)} - ${formatStation(segment.to_station_ft)}</div>
              <div><span class="font-medium">Distance:</span> ${(segment.distance_ft || 0).toFixed(1)} ft</div>
              ${segment.avg_speed_mph ? `<div><span class="font-medium">Avg Speed:</span> ${segment.avg_speed_mph.toFixed(1)} mph</div>` : ''}
            </div>
          </div>
        `
      });
    } else {
      const event = polyline.event;
      const config = EVENT_TYPES[event.event_type?.toUpperCase()] || EVENT_TYPES.BUMP;
      const isLREvent = event.event_type?.toUpperCase() === 'LR';
      setInfoWindow({
        position,
        content: `
          <div class="p-3 bg-gray-800 text-white rounded shadow-lg">
            <div class="font-bold text-lg mb-2" style="color: ${config.color}">${event.event_type}${isLREvent ? ' (ALR)' : ''}${event.wheelpath ? ` - ${event.wheelpath} Wheelpath` : ''}</div>
            ${isProjectMap && event.sessionInfo ? `<div class="text-xs text-gray-400 mb-2">Session: ${event.sessionInfo.name || event.sessionInfo.filename || 'Unnamed'}</div>` : ''}
            <div class="space-y-1 text-sm">
              <div><span class="font-medium">Magnitude:</span> ${(event.magnitude_in || 0).toFixed(2)} ${event.units || 'in'}</div>
              <div><span class="font-medium">Station:</span> ${formatStation(event.from_station_ft)} - ${formatStation(event.to_station_ft)}</div>
              <div><span class="font-medium">Length:</span> ${((event.to_station_ft || 0) - (event.from_station_ft || 0)).toFixed(1)} ft</div>
            </div>
          </div>
        `
      });
    }
  }, [EVENT_TYPES, isProjectMap]);


  const getIndicatorIcon = (indicator) => {
    const size = 24;
    const svg = `
      <svg width="${size}" height="${size}" viewBox="-12 -12 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M -8 8 L 0 -8 L 8 8 Z" fill="${indicator.color}" fill-opacity="0.9" stroke="#FFFFFF" stroke-width="1" transform="${indicator.eventType === 'DIP' ? 'rotate(180)' : ''}"/>
      </svg>`;
    return {
      url: `data:image/svg+xml;base64,${btoa(svg)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2),
    };
  };

  const adjustMap = useCallback((mode, amount) => {
    if (!mapRef.current) return;
    switch (mode) {
      case "tilt":
        mapRef.current.setTilt(Math.max(0, Math.min(67.5, mapRef.current.getTilt() + amount)));
        break;
      case "rotate":
        mapRef.current.setHeading(((mapRef.current.getHeading?.() || 0) + amount + 360) % 360);
        setCurrentHeading(mapRef.current.getHeading?.() || 0);
        break;
    }
  }, []);

  const resetNorth = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.setHeading(0);
    setCurrentHeading(0);
  }, []);

  // Arrow key controls for tilt/rotate
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      switch (e.key) {
        case 'ArrowUp': e.preventDefault(); adjustMap('tilt', -15); break;
        case 'ArrowDown': e.preventDefault(); adjustMap('tilt', 15); break;
        case 'ArrowLeft': e.preventDefault(); adjustMap('rotate', 15); break;
        case 'ArrowRight': e.preventDefault(); adjustMap('rotate', -15); break;
        default: return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [adjustMap]);

  const handleMapClick = useCallback(() => {
  if (deckClickRef.current) {
    deckClickRef.current = false;
    return;
  }

  setSelectedSegment(null);
}, []);

  const legendContentRef = useRef(null);

  const exceptionSegments = useMemo(() => segments.filter(s => s.exception), [segments]);




  if (loadError) return <div className="h-full flex items-center justify-center text-red-600">Error Loading Google Maps: {loadError.message}</div>;
  if (!isLoaded) return <div className="h-full flex items-center justify-center text-gray-600">Loading Google Maps...</div>;
  if (error) return <div className="h-full flex items-center justify-center text-red-600">{error}</div>;

  return (
    // Outer container: flex column, always 100% height
    <div ref={fullscreenContainerRef} className="relative w-full h-full flex flex-col overflow-hidden bg-gray-900">
      
      {/* Map container: animates its height */}
      <div 
      ref={mapContainerRef}
  className="relative w-full overflow-hidden transition-all duration-500 ease-in-out"
  style={{ 
    height: isKmlScrubActive
      ? 'calc(100% - 28rem)'
      : (isPlaybackActive || isDataTableActive)
        ? 'calc(100% - 16rem)'
        : '100%'
  }}
>
        
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onZoomChanged={handleZoomChanged}
          onClick={handleMapClick}
          options={mapOptions}
        >
          {/* ----- Google Maps Children (Polylines, Markers) ----- */}
          {eventPolylines
            .filter(polyline => layerVisibility[polyline.type])
            .map(polyline => (
              <PolylineF
                key={polyline.id}
                path={polyline.path}
                options={polyline.options}
                onClick={(e) => handlePolylineClick(e, polyline)}
              />
            ))}

          {indicators
            .filter(indicator => layerVisibility[indicator.eventType])
            .map(indicator => (
              <MarkerF
                key={indicator.id}
                position={indicator.position}
                icon={getIndicatorIcon(indicator)}
                zIndex={1000}
              />
            ))}

          {infoWindow && (
            <InfoWindowF
              position={infoWindow.position}
              onCloseClick={() => setInfoWindow(null)}
            >
              <div dangerouslySetInnerHTML={{ __html: infoWindow.content }} />
            </InfoWindowF>
          )}
        </GoogleMap>

        {/* ----- Map Overlays (HTML Modals, Panels, Controls) ----- */}
        {!isProjectMap && (
          <div
            className="absolute top-3 right-3 z-[1001]"
            style={{ pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <CompassRose heading={currentHeading} onResetNorth={resetNorth} />
          </div>
        )}
        
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              <span>Loading map data...</span>
            </div>
          </div>
        )}

        {selectedPhoto && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[2002]"
            onClick={() => setSelectedPhoto(null)}>
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-bold">Station {selectedPhoto.station}</h2>
                    <p className="text-sm text-gray-600">{selectedPhoto.filename}</p>
                  </div>
                  <button
                    onClick={() => setSelectedPhoto(null)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.filename}
                  className="w-full h-auto rounded"
                  style={{ maxHeight: '70vh', objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        )}


        {/* Legend */}
        <div
          className="absolute bottom-3 left-3 z-[1001]"
          style={{ pointerEvents: 'auto' }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg text-xs overflow-hidden">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegendCollapsed(!legendCollapsed); }}
              className="w-full flex items-center justify-between gap-4 px-2.5 py-1.5 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xs font-semibold text-gray-700">Legend</span>
              <svg
                className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${legendCollapsed ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              ref={legendContentRef}
              style={{
                display: 'grid',
                gridTemplateRows: legendCollapsed ? '0fr' : '1fr',
                opacity: legendCollapsed ? 0 : 1,
                transition: 'grid-template-rows 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease',
              }}
            >
              <div style={{ overflow: 'hidden' }}>
                <div className="px-3 pb-3 space-y-2">
                  {!isProjectMap && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-medium text-gray-600">MRI Quality Scale</span>
                        {legendIriFilter !== null && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setLegendIriFilter(null); }}
                            className="text-[10px] text-blue-500 hover:text-blue-700 font-medium transition-colors"
                          >
                            Show All
                          </button>
                        )}
                      </div>
                      {IRI_COLOR_SCALE.ranges.map((range, index) => {
                        const prevMax = index === 0 ? 0 : IRI_COLOR_SCALE.ranges[index - 1].max;
                        const isLast = index === IRI_COLOR_SCALE.ranges.length - 1;
                        const rangeText = isLast
                          ? `> ${prevMax}`
                          : `${prevMax}-${range.max}`;
                        const isActive = legendIriFilter === index;
                        const isDimmed = legendIriFilter !== null && !isActive;

                        return (
                          <button
                            key={index}
                            onClick={(e) => { e.stopPropagation(); setLegendIriFilter(isActive ? null : index); }}
                            className={`w-full flex items-center mt-0.5 px-1.5 py-1 rounded transition-all duration-200 ${
                              isActive
                                ? 'bg-gray-100 ring-1 ring-gray-300'
                                : isDimmed
                                  ? 'opacity-40 hover:opacity-70'
                                  : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="w-3 h-2 mr-2 rounded-sm shrink-0" style={{ backgroundColor: range.color }} />
                            <span className="text-gray-600 text-left">{range.label} ({rangeText} in/mi)</span>
                          </button>
                        );
                      })}
                      {exceptionSegments.length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setLegendIriFilter(legendIriFilter === 'exception' ? null : 'exception'); }}
                          className={`w-full flex items-center mt-0.5 px-1.5 py-1 rounded transition-all duration-200 ${
                            legendIriFilter === 'exception'
                              ? 'bg-gray-100 ring-1 ring-gray-300'
                              : legendIriFilter !== null
                                ? 'opacity-40 hover:opacity-70'
                                : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-3 h-2 mr-2 rounded-sm shrink-0" style={{ backgroundColor: IRI_COLOR_SCALE.exceptionColor }} />
                          <span className="text-gray-600 text-left">Exceptions ({exceptionSegments.length})</span>
                        </button>
                      )}
                    </div>
                  )}

                  {layerVisibility.KML && iriProfileData && (
                    <div className="border-t border-gray-200 pt-2">
                      <div className="font-medium text-gray-600 mb-1">3D KML Controls</div>
                      <div className="space-y-2">
                        {kmlProfileMode === 'iri' && (
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-gray-600">IRI Scale: {iriAltitudeScale.toFixed(2)}x</label>
                            <div className="flex items-center space-x-1 ml-2">
                              <button type="button" onClick={() => setIriAltitudeScale(prev => Math.max(0.01, Number((prev - (prev > 0.1 ? 0.1 : 0.01)).toFixed(2))))} disabled={iriAltitudeScale <= 0.01} className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">-</button>
                              <button type="button" onClick={() => setIriAltitudeScale(prev => Math.min(5.0, Number((prev + (prev >= 0.1 ? 0.1 : 0.01)).toFixed(2))))} disabled={iriAltitudeScale >= 5.0} className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">+</button>
                            </div>
                          </div>
                        )}
                        {kmlProfileMode === 'elevation' && (
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-gray-600">Vertical Scale: {elevationAltitudeScale.toFixed(1)}x</label>
                            <div className="flex items-center space-x-1 ml-2">
                              <button type="button" onClick={() => setElevationAltitudeScale(prev => Math.max(1, Number((prev - 1).toFixed(1))))} disabled={elevationAltitudeScale <= 1} className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">-</button>
                              <button type="button" onClick={() => setElevationAltitudeScale(prev => Math.min(100, Number((prev + 1).toFixed(1))))} disabled={elevationAltitudeScale >= 100} className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">+</button>
                            </div>
                          </div>
                        )}
                        <div className="pt-1 text-xs text-gray-500 space-y-1">
                          {kmlProfileMode === 'iri' && (
                            <>
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full mr-1 bg-[#00ff00]"></div>
                                <span>Left Wheelpath</span>
                              </div>
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: 'rgb(0, 100, 255)' }}></div>
                                <span>Right Wheelpath</span>
                              </div>
                              <div className="flex items-center">
                                <div className="w-3 h-0.5 mr-1" style={{ borderTop: '2px dashed rgb(255, 0, 255)' }}></div>
                                <span>Threshold (<span className="font-mono">{projectConfig?.iri_threshold_type_b?.toFixed(0) || 150}</span>)</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {kmlLoading && <div className="text-xs text-blue-600">Loading 3D KML data...</div>}
                  {kmlError && <div className="text-xs text-red-600">KML Error: {kmlError}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <ControlPanel 
        layerVisibility={layerVisibility}
        setLayerVisibility={setLayerVisibility}
        kmlProfileMode={kmlProfileMode}
        setKmlProfileMode={setKmlProfileMode}
        wheelpathFilter={wheelpathFilter}
        setWheelpathFilter={setWheelpathFilter}
        mapStyleMode={mapStyleMode}
        setMapStyleMode={setMapStyleMode}
        adjustMap={adjustMap}
        mapRef={mapRef}
        isKmlLoading={kmlLoading}
        isPhotoLoading={photoLoading}
        isPlaybackActive={isPlaybackActive}
        onStartPlayback={handleStartPlayback}
        hasPhotos={photoMarkers.length > 0 || !photoCatalogLoaded}
        iriProfileData={iriProfileData}
        elevationProfileData={elevationProfileData}
        handleStartKmlScrub={handleStartKmlScrub}
        isKmlScrubActive={isKmlScrubActive}
        segments={segments}

        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onToggleDataTable={handleToggleDataTable}
        isDataTableActive={isDataTableActive}
      />


       {selectedALR && (
  <ALRInfoPanel
    event={selectedALR}
    onClose={() => setSelectedALR(null)}
    allALREvents={filteredALREvents}
    onSelectALR={setSelectedALR}
    isReversed={isReversed}
  />
)}


{selectedSegment && (
          <SegmentInfoPanel
            segment={selectedSegment}
            onClose={() => setSelectedSegment(null)}
            allSegments={filteredSegments}
            onSelectSegment={setSelectedSegment}
            iriColorScale={IRI_COLOR_SCALE}
            alrCount={selectedSegmentALRCount}
            onViewALRs={() => {
              if (onAnalyzeSegment) {
                onAnalyzeSegment(selectedSegment.segment_id);
              }
            }}
            onShowChart={handleShowSegmentOnChart}
            isReversed={isReversed}
          />
        )}

        {selectedBumpDip && (
          <BumpDipInfoPanel
            event={selectedBumpDip}
            onClose={() => setSelectedBumpDip(null)}
            allEvents={(events || [])
              .filter(e => {
                const t = (e.event_type || '').toUpperCase();
                return t === 'BUMP' || t === 'DIP';
              })
              .sort((a, b) => (a.from_station_ft || 0) - (b.from_station_ft || 0))
            }
            onSelectEvent={(evt) => {
              setSelectedBumpDip(evt);
              const geom = parseGeometry(evt.geom);
              if (geom?.coordinates?.length && map) {
                const mid = geom.coordinates[Math.floor(geom.coordinates.length / 2)];
                map.panTo({ lat: mid[1], lng: mid[0] });
              }
            }}
            isReversed={isReversed}
          />
        )}

        {hoveredPhoto && (
          <div
            className="fixed bg-gray-900 text-white rounded-lg shadow-2xl z-[2001] pointer-events-none"
            style={{
              left: hoveredPhoto.x,
              top: hoveredPhoto.y,
              transform: 'translateY(-75%)'
            }}
          >
            <div className="p-2">
              <img
                src={hoveredPhoto.photo.thumbnailUrl}
                alt={hoveredPhoto.photo.filename}
                className="w-72 object-cover rounded"
              />
              <div className="mt-1 text-xs">
                <div className="font-medium">Station {hoveredPhoto.photo.station}</div>
                <div className="text-gray-400">{hoveredPhoto.photo.filename}</div>
              </div>
            </div>
          </div>
        )}



      </div> 
      {/* End of Map Container Div */}

      {/* The PlaybackDrawer - sibling to map container, slides in from bottom */}
     <div 
     
  className="w-full flex-shrink-0 z-[2001] transition-all duration-500 ease-in-out overflow-hidden"
  style={{ 
    height: isPlaybackActive ? '24rem' : '0rem'
  }}
>
  {photoMarkers.length > 0 && (
    <PlaybackDrawer
      photos={photoMarkers}
      currentIndex={playbackIndex}
      setCurrentIndex={setPlaybackIndex}
      isPlaying={isPlaying}
      setIsPlaying={setIsPlaying}
      playbackSpeed={playbackSpeed}
      setPlaybackSpeed={setPlaybackSpeed}
      isCameraLocked={isCameraLocked}
      setIsCameraLocked={setIsCameraLocked}
      onClose={() => setIsPlaybackActive(false)}
    />
  )}


</div>

<div 
  className="w-full flex-shrink-0 z-[2001] transition-all duration-500 ease-in-out overflow-hidden"
  style={{ 
    height: isKmlScrubActive ? '28rem' : '0rem'
  }}
>
  {isKmlScrubActive && (iriProfileData || elevationProfileData) && (
    <KmlChartsDrawer
      iriProfileData={iriProfileData}
      elevationProfileData={elevationProfileData}
      stationRange={{
        min: Math.min(...segments.map(s => s.from_station_ft)),
        max: Math.max(...segments.map(s => s.to_station_ft))
      }}
      currentStation={kmlScrubStation}
      setCurrentStation={setKmlScrubStation}
      isPlaying={isKmlScrubPlaying}
      setIsPlaying={setIsKmlScrubPlaying}
      playbackSpeed={kmlScrubSpeed}
      setPlaybackSpeed={setKmlScrubSpeed}
      isCameraLocked={isKmlScrubCameraLocked}
      setIsCameraLocked={setIsKmlScrubCameraLocked}
      onClose={() => setIsKmlScrubActive(false)}
      units="standard"
      thresholds={{ active: projectConfig?.iri_threshold_type_b || 140 }}
      alrDefects={alrDefects}
      ignoredSegments={ignoredSegments}
      surfaceEvents={surfaceEvents}
      targetZoomRange={kmlTargetRange}
      onSelectAlr={handleSelectAlrFromChart}
      segments={segments} 
  showSegments={layerVisibility.SEGMENTS}
            selectedSegment={selectedSegment} // <--- THIS WAS MISSING
            hoveredStation={hoveredStation}
              onChartHover={setHoveredStation}
              isReversed={isReversed}
    />
  )}
</div>

<div
  className="w-full flex-shrink-0 z-[2001] transition-all duration-500 ease-in-out overflow-hidden"
  style={{
    height: isDataTableActive ? '24rem' : '0rem'
  }}
>
  {isDataTableActive && (
    <DataTableDrawer
      segments={segments}
      events={events}
      iriColorScale={IRI_COLOR_SCALE}
      legendIriFilter={legendIriFilter}
      isReversed={isReversed}
      onNavigateSegment={(seg) => {
        // Ensure SEG layer is on (mutually exclusive with ALR/IRI)
        setLayerVisibility(prev => ({ ...prev, SEGMENTS: true, LR: false, IRI_ROADWAY: false }));
        setSelectedALR(null);
        setSelectedBumpDip(null);
        setSelectedSegment(seg);
        const geom = parseGeometry(seg.geom);
        if (geom?.coordinates?.length && map) {
          const bounds = new window.google.maps.LatLngBounds();
          geom.coordinates.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
          map.fitBounds(bounds, 100);
          map.setTilt(45);
        }
      }}
      onNavigateEvent={(evt, eventType) => {
        // Enable the appropriate layer
        if (eventType === 'LR') {
          setLayerVisibility(prev => ({ ...prev, LR: true, SEGMENTS: false, IRI_ROADWAY: false }));
          setSelectedSegment(null);
          setSelectedBumpDip(null);
          setSelectedALR(evt);
        } else if (eventType === 'BUMP') {
          setLayerVisibility(prev => ({ ...prev, BUMP: true }));
          setSelectedSegment(null);
          setSelectedALR(null);
          setSelectedBumpDip(evt);
        } else if (eventType === 'DIP') {
          setLayerVisibility(prev => ({ ...prev, DIP: true }));
          setSelectedSegment(null);
          setSelectedALR(null);
          setSelectedBumpDip(evt);
        }
        if (evt.geom && map) {
          const geom = parseGeometry(evt.geom);
          if (geom?.coordinates?.length) {
            const bounds = new window.google.maps.LatLngBounds();
            geom.coordinates.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
            map.fitBounds(bounds, 100);
          map.setTilt(45);
          }
        }
      }}
      onClose={() => setIsDataTableActive(false)}
    />
  )}
</div>

    </div> // End of main parent div
  );
}

export default FusionMap;
